import React, { useState, useEffect, useContext, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCheck,
  faTimes,
  faDownload,
  faTrash,
  faSpinner,
  faHome,
  faUser,
  faWallet,
  faCog,
  faChartLine,
  faMoneyBill,
  faCalendarAlt,
  faSearch,
  faFilter,
  faPlus,
  faReceipt,
  faExchangeAlt,
  faHistory,
  faChevronRight,
  faDollarSign,
  faArrowDown,
  faArrowUp,
  faCoins,
  faChartBar,
  faChartPie,
  faMapMarkerAlt,
  faBuilding,
  faCalendarDay,
  faCalendarWeek,
  faCalendarCheck,
  faEdit,
  faInfoCircle,
  faLock,
  faUnlock,
  faUserPlus,
  faUserMinus,
  faFileAlt,
  faCaretDown,
  faCaretUp
} from '@fortawesome/free-solid-svg-icons';
import TopBar from '../TopBar';
import LeftMenu, { LoginPrompt } from '../LeftMenu';
import RightMenu from '../RightMenu';
import { UserContext } from '../../context/UserContext';
import { TourContext } from '../../context/TourContext';
import { getFirestore, doc, setDoc, getDoc, updateDoc, collection, addDoc, getDocs, query, orderBy, where, deleteDoc, onSnapshot, Timestamp, serverTimestamp, writeBatch } from 'firebase/firestore';
import defaultUserProfilePhoto from '../../assets/Images/defaultUserProfilePicture.png';
import styles from './Expense.module.css';
import * as XLSX from 'xlsx';
import moment from 'moment';
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, 
  LineChart, Line, PieChart, Pie, Cell, Area, AreaChart
} from 'recharts';
// Import notification service for sending notifications
import { createNotification } from '../../utils/NotificationService';

const MAX_NOTE_LENGTH = 50;

// Financial categories
const expenseCategories = [
  { id: 'all', name: 'All' },
  { id: 'Food', name: 'Food & Drinks' },
  { id: 'Transport', name: 'Transport' },
  { id: 'Accommodation', name: 'Accommodation' },
  { id: 'Equipment', name: 'Equipment' },
  { id: 'Venue', name: 'Venue' },
  { id: 'Other', name: 'Other' }
];

const earningCategories = [
  { id: 'Performance', name: 'Performance Fee' },
  { id: 'Merchandise', name: 'Merchandise Sales' },
  { id: 'Royalty', name: 'Royalties' },
  { id: 'Sponsorship', name: 'Sponsorship' },
  { id: 'Other', name: 'Other Income' }
];

// Colors for charts
const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d'];

const Financial = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { userData, getUserEvents } = useContext(UserContext);
  const { selectedEvent, events, selectedDate, setSelectedDate, updateDate } = useContext(TourContext);

  // Basic UI state
  const [activeTab, setActiveTab] = useState('record');
  const [activeRecordTab, setActiveRecordTab] = useState('expense');
  const [activeAnalyticsTab, setActiveAnalyticsTab] = useState('summary');
  const [activeHistoryTab, setActiveHistoryTab] = useState('all');
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [isLoadingFinancials, setIsLoadingFinancials] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSettling, setIsSettling] = useState(false);
  const [totalAmountOwed, setTotalAmountOwed] = useState(0);
  
  // NEW: Event and date selection state
  const [selectedEventForRecord, setSelectedEventForRecord] = useState(null);
  const [userEvents, setUserEvents] = useState([]);
  const [selectedEventForHistory, setSelectedEventForHistory] = useState(null);
  
  // Expense specific state
  const [amount, setAmount] = useState('');
  const [selectedDateValue, setSelectedDateValue] = useState('');
  const [note, setNote] = useState('');
  const [currency, setCurrency] = useState('₹');
  const [whoOwes, setWhoOwes] = useState([]);
  const [isSelfExpense, setIsSelfExpense] = useState(false);
  const [filterCategory, setFilterCategory] = useState('all');
  
  // Earning specific state
  const [earningAmount, setEarningAmount] = useState('');
  const [earningSource, setEarningSource] = useState('');
  const [earningCategory, setEarningCategory] = useState('Performance');
  const [earningNote, setEarningNote] = useState('');
  // NEW: Added state for recording earnings for other users
  const [earningForUserId, setEarningForUserId] = useState('');
  const [isSelfEarning, setIsSelfEarning] = useState(true);
  const [hasFinancialPermission, setHasFinancialPermission] = useState(false);
  
  // History view state
  const [selectedHistoryDate, setSelectedHistoryDate] = useState(null);
  const [datesWithFinancials, setDatesWithFinancials] = useState([]);
  const [financialHistory, setFinancialHistory] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [showOnlyUserFinancials, setShowOnlyUserFinancials] = useState(false);
  const [showChartsInHistory, setShowChartsInHistory] = useState(false);
  const [auditTrail, setAuditTrail] = useState({});
  const [showDeletedEntries, setShowDeletedEntries] = useState(false);
  
  // Summary state
  const [totalExpense, setTotalExpense] = useState(0);
  const [totalRecoverableExpense, setTotalRecoverableExpense] = useState(0);
  const [totalEarnings, setTotalEarnings] = useState(0);
  const [netEarnings, setNetEarnings] = useState(0);
  const [detailedExpenses, setDetailedExpenses] = useState([]);
  const [detailedEarnings, setDetailedEarnings] = useState([]);
  const [detailedOwed, setDetailedOwed] = useState([]);
  
  // Report generation state
  const [selectedDatesForReport, setSelectedDatesForReport] = useState([]);
  const [selectedEventsForReport, setSelectedEventsForReport] = useState([]);
  const [showDateSelection, setShowDateSelection] = useState(false);
  const [reportType, setReportType] = useState('dates'); // 'dates', 'events', 'year'
  
  // Analytics data state
  const [analyticsData, setAnalyticsData] = useState({
    byDate: [],
    byMonth: [],
    byYear: [],
    byEvent: [],
    byCity: []
  });
  const [isLoadingAnalytics, setIsLoadingAnalytics] = useState(false);
  const [selectedEventForEventAnalytics, setSelectedEventForEventAnalytics] = useState(null);
  const [moreEventsVisible, setMoreEventsVisible] = useState(false);

  // State for showing audit history
  const [showAuditHistory, setShowAuditHistory] = useState({});
  const [isEditing, setIsEditing] = useState(null);
  const [editedAmount, setEditedAmount] = useState('');
  const [editedNote, setEditedNote] = useState('');

  // Listen for window resize to determine mobile view
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // Load user events on initial mount
  useEffect(() => {
    if (userData?.userId) {
      fetchUserEvents();
    }
  }, [userData?.userId]);

  // Handle navigation from notifications
useEffect(() => {
  if (location.state?.fromNotification) {
    const { notificationEventId, notificationEventType, notificationDateValue } = location.state;
    
    // Find the target event based on the event ID from notification
    const targetEvent = userEvents.find(event => 
      event.id === notificationEventId && 
      event.type === notificationEventType
    );
    
    if (targetEvent) {
      // Set the event for history view
      setSelectedEventForHistory(targetEvent);
      
      // Find date document that matches the date string
      const dateDoc = targetEvent.eventDates?.find(date => date.date === notificationDateValue);
      
      // If we found the date, update the UI to show this transaction
      if (dateDoc) {
        // Set active tab to history
        setActiveTab('history');
        
        // Set the date for history view with small delay to ensure event is selected first
        setTimeout(() => {
          setSelectedHistoryDate(notificationDateValue);
        }, 100);
      }
    }
  }
}, [location.state, userEvents]);

  // Fetch all events the user is assigned to
  const fetchUserEvents = async () => {
    if (!userData?.userId) return;
    
    try {
      const events = await getUserEvents(userData.userId);
      setUserEvents(events.filter(Boolean));
      
      // Set selected event for record if none is selected
      if (!selectedEventForRecord && events.length > 0) {
        setSelectedEventForRecord(selectedEvent || events[0]);
      }
      
      // Set selected event for history if none is selected
      if (!selectedEventForHistory && events.length > 0) {
        setSelectedEventForHistory(selectedEvent || events[0]);
      }
    } catch (error) {
      console.error('Error fetching user events:', error);
    }
  };

  // Check if user has financial permission for selected event
  useEffect(() => {
    const checkFinancialPermission = async () => {
      if (!userData?.userId || !selectedEventForRecord?.id) return;
      
      try {
        const db = getFirestore();
        const eventType = selectedEventForRecord.type || 'tour';
        const collectionName = eventType === 'tour' ? 'tours' : 'shows';

        // Check if user is the creator of the event
        const eventDoc = await getDoc(doc(db, collectionName, selectedEventForRecord.id));
        if (eventDoc.exists()) {
          const eventData = eventDoc.data();
          
          // If user is the creator, they have permission
          if (eventData.createdBy?.userId === userData.userId) {
            setHasFinancialPermission(true);
            return;
          }
          
          // Check assigned permissions from assignedUsers subcollection
          const userPermissionsDoc = await getDoc(
            doc(db, collectionName, selectedEventForRecord.id, 'assignedUsers', userData.userId)
          );
          
          if (userPermissionsDoc.exists()) {
            const permissionsData = userPermissionsDoc.data();
            // Check if the user has financial management permission
            if (permissionsData.permissions?.canManageFinancials) {
              setHasFinancialPermission(true);
              return;
            }
          }
          
          // No permissions found
          setHasFinancialPermission(false);
        }
      } catch (error) {
        console.error('Error checking financial permissions:', error);
        setHasFinancialPermission(false);
      }
    };
    
    checkFinancialPermission();
  }, [selectedEventForRecord, userData]);

  // All users from the event - with null check for userData
  const allUsers = useMemo(() => {
    // Return empty array if userData or selectedEvent is null
    if (!userData || !selectedEvent) return [];

    const userMap = new Map();

    // Add current user
    userMap.set(userData.userId, {
      userId: userData.userId,
      fullName: userData.fullName,
      profilePictureUrl: userData.profilePictureUrl,
      isCurrentUser: true
    });

    // Check if we have assignedUsers subcollection data
    if (selectedEvent.assignedUsers && Array.isArray(selectedEvent.assignedUsers)) {
      selectedEvent.assignedUsers.forEach(user => {
        if (!userMap.has(user.userId)) {
          userMap.set(user.userId, {
            ...user,
            fullName: user.fullName || user.name,
            isCurrentUser: user.userId === userData.userId
          });
        }
      });
    } else {
      // Fallback to old structure for backward compatibility
      // Add artists
      selectedEvent.artistDetails?.forEach(artist => {
        if (!userMap.has(artist.userId)) {
          userMap.set(artist.userId, {
            ...artist,
            fullName: artist.fullName || artist.name,
            isCurrentUser: artist.userId === userData.userId
          });
        }
      });

      // Add crew
      selectedEvent.crewDetails?.forEach(crew => {
        if (!userMap.has(crew.userId)) {
          userMap.set(crew.userId, {
            ...crew,
            fullName: crew.fullName || crew.name,
            isCurrentUser: crew.userId === userData.userId
          });
        }
      });

      // Add Manager
      if (selectedEvent.createdBy) {
        const manager = selectedEvent.createdBy;
        if (!userMap.has(manager.userId)) {
          userMap.set(manager.userId, {
            userId: manager.userId,
            fullName: manager.fullName,
            isCurrentUser: manager.userId === userData.userId,
            userType: "Manager"
          });
        }
      }
    }

    return Array.from(userMap.values());
  }, [selectedEvent, userData]);

  // Update selected event for record and load its dates
  useEffect(() => {
    if (selectedEventForRecord) {
      // Reset date selection
      setSelectedDateValue('');
      setSelectedDate('');
      
      // Reset earnings user selection on event change
      setEarningForUserId(userData?.userId || '');
      setIsSelfEarning(true);
    }
  }, [selectedEventForRecord, userData]);
  
  // Update selected event for history and load its financial history
  useEffect(() => {
    if (selectedEventForHistory) {
      // Reset history view
      setSelectedHistoryDate(null);
      
      // Fetch dates with financials for this event
      fetchDatesWithFinancials(selectedEventForHistory.id);
    }
  }, [selectedEventForHistory]);

  // UPDATED: Comprehensive venue details extraction
  const getVenueDetails = (dateObj) => {
    if (!dateObj) return {};
    
    // Check all possible locations where venue data might be stored
    const venueData = dateObj.venueData || {};
    const venueDetails = dateObj.venueDetails || {};
    const venue = dateObj.venue || {};
    const event = dateObj.event || {};
    
    // First try the structure seen in Crew.js (date.event.venueDetails.venueData)
    if (event.venueDetails?.venueData?.displayName) {
      return {
        venueName: event.venueDetails.venueData.displayName || 'Unknown Venue',
        city: event.venueDetails.venueData.city || '',
        state: event.venueDetails.venueData.state || '',
        country: event.venueDetails.venueData.country || '',
        formattedAddress: event.venueDetails.venueData.formattedAddress || ''
      };
    }
    
 
    
    // Try venueData object
    if (venueData.displayName || venueData.formattedAddress) {
      return {
        venueName: venueData.displayName || 'Unknown Venue',
        city: venueData.city || '',
        state: venueData.state || '',
        country: venueData.country || '',
        formattedAddress: venueData.formattedAddress || ''
      };
    }
    
    // Try venueDetails object
    if (venueDetails.displayName || venueDetails.formattedAddress) {
      return {
        venueName: venueDetails.displayName || 'Unknown Venue',
        city: venueDetails.city || '',
        state: venueDetails.state || '',
        country: venueDetails.country || '',
        formattedAddress: venueDetails.formattedAddress || ''
      };
    }
    
    // Try event.venueDetails (another possible location)
    if (event.venueDetails) {
      const eventVenue = event.venueDetails;
      return {
        venueName: eventVenue.displayName || eventVenue.venueName || 'Unknown Venue',
        city: eventVenue.city || '',
        state: eventVenue.state || '',
        country: eventVenue.country || '',
        formattedAddress: eventVenue.formattedAddress || ''
      };
    }
    
    // Try venue object (old structure)
    if (venue.venueName || venue.formattedAddress) {
      return {
        venueName: venue.venueName || venue.displayName || 'Unknown Venue',
        city: venue.city || '',
        state: venue.state || '',
        country: venue.country || '',
        formattedAddress: venue.formattedAddress || ''
      };
    }
    
    // If nothing found, return default empty values
    return {
      venueName: 'Unknown Venue',
      city: '',
      state: '',
      country: '',
      formattedAddress: 'No address available'
    };
  };

  const getFormattedDate = (dateObj) => {
    if (dateObj.rescheduleDate?.isRescheduled) {
      return new Date(dateObj.rescheduleDate.newDate);
    }
    return new Date(dateObj.date);
  };
  
  // Reset form fields
  const resetForm = () => {
    // Reset expense fields
    setAmount('');
    setSelectedDateValue('');
    setNote('');
    setCurrency('₹');
    setWhoOwes([]);
    setIsSelfExpense(false);
    setFilterCategory('Other');
    
    // Reset earning fields
    setEarningAmount('');
    setEarningSource('');
    setEarningNote('');
    setEarningCategory('Performance');
    setEarningForUserId(userData?.userId || '');
    setIsSelfEarning(true);
  };

  // Handle date selection in the form
  const handleDateChange = (e) => {
    const selectedValue = e.target.value;
    setSelectedDateValue(selectedValue);
    
    // Find the corresponding date document ID
    if (selectedEventForRecord?.eventDates) {
      const selectedDateDoc = selectedEventForRecord.eventDates.find(date => date.date === selectedValue);
      if (selectedDateDoc) {
        setSelectedDate(selectedDateDoc.id);
      }
    }
  };

  // Handle user selection for expense splitting
  const toggleUserOwes = (user) => {
    setWhoOwes(prevUsers => {
      if (prevUsers.some(u => u.userId === user.userId)) {
        return prevUsers.filter(u => u.userId !== user.userId);
      } else {
        const equalSplit = amount ? parseFloat(amount) / (prevUsers.length + 1) : 0;
        return [...prevUsers, { ...user, amountOwed: equalSplit.toFixed(2) }];
      }
    });
  };

  // Update individual amount owed
  const updateUserAmountOwed = (userId, newAmount) => {
    setWhoOwes(prevUsers => prevUsers.map(user =>
      user.userId === userId ? { ...user, amountOwed: newAmount } : user
    ));
  };

  // Fetch financial history and summary data when event or date changes
  useEffect(() => {
    if (!userData || !selectedEvent?.id || !selectedDate) return;
    
    fetchFinancialHistory(selectedEvent.id, selectedDate);
    fetchGlobalFinancialSummary();
  }, [selectedEvent, selectedDate, userData]);

  useEffect(() => {
    // Reset history-related states when event changes
    setSelectedHistoryDate(null);
    setActiveTab('record');
  }, [selectedEvent]);

  // Update split amounts when total amount changes
  useEffect(() => {
    if (amount && whoOwes.length > 0) {
      const equalSplit = parseFloat(amount) / whoOwes.length;
      setWhoOwes(prevUsers => prevUsers.map(user => ({
        ...user,
        amountOwed: equalSplit.toFixed(2)
      })));
    }
  }, [amount, whoOwes.length]);

  // Update dates list when financial history changes or filter changes
  useEffect(() => {
    if (!selectedEventForHistory?.id || !userData) return;
    
    const loadDates = async () => {
      const dates = await fetchDatesWithFinancials(selectedEventForHistory.id);
      setDatesWithFinancials(dates);
    };
    
    loadDates();
  }, [selectedEventForHistory, userData, showOnlyUserFinancials, financialHistory]);

  // Load analytics data when tab changes to analytics
  useEffect(() => {
    if (activeTab === 'analytics' && userData) {
      fetchAnalyticsData();
    }
  }, [activeTab, userData?.userId]);

  // Filter financials based on search query, category, and user preference
  const filteredFinancials = useMemo(() => {
    if (!selectedHistoryDate || !financialHistory[selectedHistoryDate]) {
      return { expenses: [], earnings: [] };
    }
    
    let filteredExpenses = financialHistory[selectedHistoryDate].expenses || [];
    let filteredEarnings = financialHistory[selectedHistoryDate].earnings || [];
    
    // Apply user filter if enabled
    if (showOnlyUserFinancials) {
      filteredExpenses = filteredExpenses.filter(expense => 
        // User is the payer
        expense.paidBy === userData.userId || 
        // User owes money
        expense.whoOwes?.some(ower => ower.userId === userData.userId)
      );
      
      filteredEarnings = filteredEarnings.filter(earning => 
        // User earned the money
        earning.earnedBy === userData.userId
      );
    }
    
    // Apply category filter to expenses
    if (filterCategory !== 'all' && activeHistoryTab !== 'earnings') {
      filteredExpenses = filteredExpenses.filter(expense => expense.category === filterCategory);
    }
    
    // Apply category filter to earnings
    if (filterCategory !== 'all' && activeHistoryTab !== 'expenses') {
      filteredEarnings = filteredEarnings.filter(earning => earning.category === filterCategory);
    }
    
    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      
      filteredExpenses = filteredExpenses.filter(expense => 
        expense.note?.toLowerCase().includes(query) ||
        allUsers.find(u => u.userId === expense.paidBy)?.fullName.toLowerCase().includes(query) ||
        expense.whoOwes?.some(ower => ower.fullName.toLowerCase().includes(query)) ||
        expense.category?.toLowerCase().includes(query)
      );
      
      filteredEarnings = filteredEarnings.filter(earning => 
        earning.note?.toLowerCase().includes(query) ||
        earning.source?.toLowerCase().includes(query) ||
        allUsers.find(u => u.userId === earning.earnedBy)?.fullName.toLowerCase().includes(query) ||
        earning.category?.toLowerCase().includes(query)
      );
    }

    // Filter out deleted entries if not showing deleted
    if (!showDeletedEntries) {
      filteredExpenses = filteredExpenses.filter(expense => !expense.isDeleted);
      filteredEarnings = filteredEarnings.filter(earning => !earning.isDeleted);
    }
    
    return {
      expenses: filteredExpenses,
      earnings: filteredEarnings
    };
  }, [
    selectedHistoryDate, financialHistory, filterCategory, searchQuery, 
    allUsers, userData, showOnlyUserFinancials, activeHistoryTab,
    showDeletedEntries
  ]);
  
  // Prepare data for chart based on financial history
  const chartData = useMemo(() => {
    if (!selectedHistoryDate || !financialHistory[selectedHistoryDate]) {
      return {
        summary: [],
        expensesByCategory: [],
        earningsByCategory: []
      };
    }
    
    // Filter out deleted entries for charts
    const expenses = (financialHistory[selectedHistoryDate].expenses || [])
      .filter(expense => !expense.isDeleted);
    const earnings = (financialHistory[selectedHistoryDate].earnings || [])
      .filter(earning => !earning.isDeleted);
    
    // Combine and calculate aggregated data
    const totalExpenses = expenses.reduce((sum, expense) => sum + parseFloat(expense.amount), 0);
    const totalEarnings = earnings.reduce((sum, earning) => sum + parseFloat(earning.amount), 0);
    
    // Category breakdown for expenses
    const expensesByCategory = expenses.reduce((categories, expense) => {
      const category = expense.category || 'Other';
      if (!categories[category]) {
        categories[category] = 0;
      }
      categories[category] += parseFloat(expense.amount);
      return categories;
    }, {});
    
    // Category breakdown for earnings
    const earningsByCategory = earnings.reduce((categories, earning) => {
      const category = earning.category || 'Other';
      if (!categories[category]) {
        categories[category] = 0;
      }
      categories[category] += parseFloat(earning.amount);
      return categories;
    }, {});
    
    return {
      summary: [
        { name: 'Expenses', value: totalExpenses },
        { name: 'Earnings', value: totalEarnings }
      ],
      expensesByCategory: Object.entries(expensesByCategory).map(([name, value]) => ({ name, value })),
      earningsByCategory: Object.entries(earningsByCategory).map(([name, value]) => ({ name, value }))
    };
  }, [selectedHistoryDate, financialHistory]);

  // Fetch global financial summary from new structure
  const fetchGlobalFinancialSummary = async () => {
    if (!userData?.userId) return;
    
    try {
      const db = getFirestore();
      
      // Reference to the user's document in financialSummary collection
      const summaryDocRef = doc(db, 'financialSummary', userData.userId);
      
      // Get the document
      const summaryDoc = await getDoc(summaryDocRef);
      
      if (summaryDoc.exists()) {
        // Get global totals
        const summaryData = summaryDoc.data();
        
        setTotalExpense(summaryData.totalExpense || 0);
        setTotalRecoverableExpense(summaryData.totalRecoverableExpense || 0);
        setTotalEarnings(summaryData.totalEarnings || 0);
        setNetEarnings((summaryData.totalEarnings || 0) - (summaryData.totalExpense || 0));
      } else {
        // If document doesn't exist yet, calculate from financial history
        calculateFinancialTotals(financialHistory);
        
        // Create the document with initial values
        await setDoc(summaryDocRef, {
          totalExpense: totalExpense,
          totalRecoverableExpense: totalRecoverableExpense, 
          totalEarnings: totalEarnings,
          netEarnings: netEarnings,
          createdAt: serverTimestamp(),
          lastUpdated: serverTimestamp()
        });
      }
      
      // Set up real-time listener for global summary
      const unsubscribe = onSnapshot(summaryDocRef, (doc) => {
        if (doc.exists()) {
          const data = doc.data();
          setTotalExpense(data.totalExpense || 0);
          setTotalRecoverableExpense(data.totalRecoverableExpense || 0);
          setTotalEarnings(data.totalEarnings || 0);
          setNetEarnings((data.totalEarnings || 0) - (data.totalExpense || 0));
        }
      });
      
      return unsubscribe;
    } catch (error) {
      console.error('Error fetching global financial summary:', error);
      calculateFinancialTotals(financialHistory);
    }
  };

  // Update financial summary in the new collection structure
  const updateFinancialSummaryCollection = async (db, userId, eventId, eventName, dateValue, venueDetails, recordType, recordAmount, multiplier, recordData, isSettlement = false, settledAmount = 0) => {
    try {
      // Use a batch for all updates to ensure atomicity
      const batch = writeBatch(db);
      
      // Extract date components
      const date = new Date(dateValue);
      const year = date.getFullYear().toString();
      const month = date.toLocaleString('en-US', { month: 'long' }).toLowerCase();
      const month_year = `${month}_${year}`;
      const date_eventId = `${dateValue}_${eventId}`;
      
      // Format city key
      const city = venueDetails.city || 'Unknown';
      const state = venueDetails.state || '';
      const country = venueDetails.country || '';
      const city_state_country = [city, state, country].filter(Boolean).join('_').toLowerCase();
      
      // 1. Update global financial summary
      const globalSummaryRef = doc(db, 'financialSummary', userId);
      const globalSummaryDoc = await getDoc(globalSummaryRef);
      
      if (globalSummaryDoc.exists()) {
        const summaryData = globalSummaryDoc.data();
        
        let updatedTotalExpense = summaryData.totalExpense || 0;
        let updatedTotalRecoverableExpense = summaryData.totalRecoverableExpense || 0;
        let updatedTotalEarnings = summaryData.totalEarnings || 0;
        
        if (recordType === 'expense') {
          if (isSettlement) {
            // For settlements, reduce recoverable expense
            updatedTotalRecoverableExpense -= (settledAmount * multiplier);
          } else {
            updatedTotalExpense += (recordAmount * multiplier);
            
            // If this is not a self expense, add to recoverable expenses
            if (!isSettlement && recordData && !recordData.isSelfExpense) {
              const recoverableAmount = recordData.whoOwes
                ?.filter(ower => ower.userId !== userId)
                .reduce((sum, ower) => sum + parseFloat(ower.amountOwed), 0) || 0;
                
              updatedTotalRecoverableExpense += (recoverableAmount * multiplier);
            }
          }
        } else {
          updatedTotalEarnings += (recordAmount * multiplier);
        }
        
        const updatedNetEarnings = updatedTotalEarnings - updatedTotalExpense;
        
        batch.update(globalSummaryRef, {
          totalExpense: updatedTotalExpense,
          totalRecoverableExpense: updatedTotalRecoverableExpense,
          totalEarnings: updatedTotalEarnings,
          netEarnings: updatedNetEarnings,
          lastUpdated: serverTimestamp()
        });
      } else {
        // Create new document
        const initialTotalExpense = recordType === 'expense' ? recordAmount * multiplier : 0;
        const initialTotalRecoverableExpense = (recordType === 'expense' && recordData && !recordData.isSelfExpense) ? 
          (recordData.whoOwes?.filter(ower => ower.userId !== userId).reduce((sum, ower) => sum + parseFloat(ower.amountOwed), 0) || 0) * multiplier : 0;
        const initialTotalEarnings = recordType === 'earning' ? recordAmount * multiplier : 0;
        
        batch.set(globalSummaryRef, {
          totalExpense: initialTotalExpense,
          totalRecoverableExpense: initialTotalRecoverableExpense,
          totalEarnings: initialTotalEarnings,
          netEarnings: initialTotalEarnings - initialTotalExpense,
          createdAt: serverTimestamp(),
          lastUpdated: serverTimestamp()
        });
      }
      
      // 2. Update date breakdown
      const dateBreakdownRef = doc(db, 'financialSummary', userId, 'dateBreakdown', date_eventId);
      const dateBreakdownDoc = await getDoc(dateBreakdownRef);
      
      if (dateBreakdownDoc.exists()) {
        const dateData = dateBreakdownDoc.data();
        
        let updatedExpenses = dateData.expenses || 0;
        let updatedEarnings = dateData.earnings || 0;
        
        if (recordType === 'expense') {
          updatedExpenses += (recordAmount * multiplier);
        } else {
          updatedEarnings += (recordAmount * multiplier);
        }
        
        batch.update(dateBreakdownRef, {
          expenses: updatedExpenses,
          earnings: updatedEarnings,
          netEarnings: updatedEarnings - updatedExpenses,
          date: dateValue,
          eventId: eventId,
          eventName: eventName,
          venueName: venueDetails.venueName,
          formattedAddress: venueDetails.formattedAddress,
          city: venueDetails.city,
          state: venueDetails.state,
          country: venueDetails.country,
          lastUpdated: serverTimestamp()
        });
      } else {
        const initialExpenses = recordType === 'expense' ? recordAmount * multiplier : 0;
        const initialEarnings = recordType === 'earning' ? recordAmount * multiplier : 0;
        
        batch.set(dateBreakdownRef, {
          expenses: initialExpenses,
          earnings: initialEarnings,
          netEarnings: initialEarnings - initialExpenses,
          date: dateValue,
          eventId: eventId,
          eventName: eventName,
          venueName: venueDetails.venueName,
          formattedAddress: venueDetails.formattedAddress,
          city: venueDetails.city,
          state: venueDetails.state,
          country: venueDetails.country,
          createdAt: serverTimestamp(),
          lastUpdated: serverTimestamp()
        });
      }
      
      // 3. Update month breakdown
      const monthBreakdownRef = doc(db, 'financialSummary', userId, 'monthBreakdown', month_year);
      const monthBreakdownDoc = await getDoc(monthBreakdownRef);
      
      if (monthBreakdownDoc.exists()) {
        const monthData = monthBreakdownDoc.data();
        
        let updatedExpenses = monthData.expenses || 0;
        let updatedEarnings = monthData.earnings || 0;
        
        if (recordType === 'expense') {
          updatedExpenses += (recordAmount * multiplier);
        } else {
          updatedEarnings += (recordAmount * multiplier);
        }
        
        // Update or create date entry in dates map
        const dates = monthData.dates || {};
        const dateEntry = dates[dateValue] || { expenses: 0, earnings: 0, netEarnings: 0 };
        
        if (recordType === 'expense') {
          dateEntry.expenses += (recordAmount * multiplier);
        } else {
          dateEntry.earnings += (recordAmount * multiplier);
        }
        
        dateEntry.netEarnings = dateEntry.earnings - dateEntry.expenses;
        dates[dateValue] = dateEntry;
        
        batch.update(monthBreakdownRef, {
          expenses: updatedExpenses,
          earnings: updatedEarnings,
          netEarnings: updatedEarnings - updatedExpenses,
          month: month,
          year: year,
          dates: dates,
          lastUpdated: serverTimestamp()
        });
      } else {
        const initialExpenses = recordType === 'expense' ? recordAmount * multiplier : 0;
        const initialEarnings = recordType === 'earning' ? recordAmount * multiplier : 0;
        
        const dates = {};
        dates[dateValue] = {
          expenses: initialExpenses,
          earnings: initialEarnings,
          netEarnings: initialEarnings - initialExpenses
        };
        
        batch.set(monthBreakdownRef, {
          expenses: initialExpenses,
          earnings: initialEarnings,
          netEarnings: initialEarnings - initialExpenses,
          month: month,
          year: year,
          dates: dates,
          createdAt: serverTimestamp(),
          lastUpdated: serverTimestamp()
        });
      }
      
      // 4. Update year breakdown
      const yearBreakdownRef = doc(db, 'financialSummary', userId, 'yearBreakdown', year);
      const yearBreakdownDoc = await getDoc(yearBreakdownRef);
      
      if (yearBreakdownDoc.exists()) {
        const yearData = yearBreakdownDoc.data();
        
        let updatedExpenses = yearData.expenses || 0;
        let updatedEarnings = yearData.earnings || 0;
        
        if (recordType === 'expense') {
          updatedExpenses += (recordAmount * multiplier);
        } else {
          updatedEarnings += (recordAmount * multiplier);
        }
        
        // Update or create month entry in months map
        const months = yearData.months || {};
        const monthEntry = months[month] || { expenses: 0, earnings: 0, netEarnings: 0 };
        
        if (recordType === 'expense') {
          monthEntry.expenses += (recordAmount * multiplier);
        } else {
          monthEntry.earnings += (recordAmount * multiplier);
        }
        
        monthEntry.netEarnings = monthEntry.earnings - monthEntry.expenses;
        months[month] = monthEntry;
        
        batch.update(yearBreakdownRef, {
          expenses: updatedExpenses,
          earnings: updatedEarnings,
          netEarnings: updatedEarnings - updatedExpenses,
          year: year,
          months: months,
          lastUpdated: serverTimestamp()
        });
      } else {
        const initialExpenses = recordType === 'expense' ? recordAmount * multiplier : 0;
        const initialEarnings = recordType === 'earning' ? recordAmount * multiplier : 0;
        
        const months = {};
        months[month] = {
          expenses: initialExpenses,
          earnings: initialEarnings,
          netEarnings: initialEarnings - initialExpenses
        };
        
        batch.set(yearBreakdownRef, {
          expenses: initialExpenses,
          earnings: initialEarnings,
          netEarnings: initialEarnings - initialExpenses,
          year: year,
          months: months,
          createdAt: serverTimestamp(),
          lastUpdated: serverTimestamp()
        });
      }
      
      // 5. Update event breakdown
      const eventBreakdownRef = doc(db, 'financialSummary', userId, 'eventBreakdown', eventId);
      const eventBreakdownDoc = await getDoc(eventBreakdownRef);
      
      if (eventBreakdownDoc.exists()) {
        const eventData = eventBreakdownDoc.data();
        
        let updatedExpenses = eventData.expenses || 0;
        let updatedEarnings = eventData.earnings || 0;
        
        if (recordType === 'expense') {
          updatedExpenses += (recordAmount * multiplier);
        } else {
          updatedEarnings += (recordAmount * multiplier);
        }
        
        // Update or create date entry in dates map
        const dates = eventData.dates || {};
        const dateEntry = dates[dateValue] || { expenses: 0, earnings: 0, netEarnings: 0 };
        
        if (recordType === 'expense') {
          dateEntry.expenses += (recordAmount * multiplier);
        } else {
          dateEntry.earnings += (recordAmount * multiplier);
        }
        
        dateEntry.netEarnings = dateEntry.earnings - dateEntry.expenses;
        dates[dateValue] = dateEntry;
        
        batch.update(eventBreakdownRef, {
          expenses: updatedExpenses,
          earnings: updatedEarnings,
          netEarnings: updatedEarnings - updatedExpenses,
          eventName: eventName,
          dates: dates,
          lastUpdated: serverTimestamp()
        });
      } else {
        const initialExpenses = recordType === 'expense' ? recordAmount * multiplier : 0;
        const initialEarnings = recordType === 'earning' ? recordAmount * multiplier : 0;
        
        const dates = {};
        dates[dateValue] = {
          expenses: initialExpenses,
          earnings: initialEarnings,
          netEarnings: initialEarnings - initialExpenses
        };
        
        batch.set(eventBreakdownRef, {
          expenses: initialExpenses,
          earnings: initialEarnings,
          netEarnings: initialEarnings - initialExpenses,
          eventName: eventName,
          dates: dates,
          createdAt: serverTimestamp(),
          lastUpdated: serverTimestamp()
        });
      }
      
      // 6. Update city breakdown
      if (city_state_country !== 'unknown__') {
        const cityBreakdownRef = doc(db, 'financialSummary', userId, 'cityBreakdown', city_state_country);
        const cityBreakdownDoc = await getDoc(cityBreakdownRef);
        
        if (cityBreakdownDoc.exists()) {
          const cityData = cityBreakdownDoc.data();
          
          let updatedExpenses = cityData.expenses || 0;
          let updatedEarnings = cityData.earnings || 0;
          
          if (recordType === 'expense') {
            updatedExpenses += (recordAmount * multiplier);
          } else {
            updatedEarnings += (recordAmount * multiplier);
          }
          
          // Update or create date entry in dates map
          const dates = cityData.dates || {};
          const dateEntry = dates[dateValue] || { expenses: 0, earnings: 0, netEarnings: 0 };
          
          if (recordType === 'expense') {
            dateEntry.expenses += (recordAmount * multiplier);
          } else {
            dateEntry.earnings += (recordAmount * multiplier);
          }
          
          dateEntry.netEarnings = dateEntry.earnings - dateEntry.expenses;
          dates[dateValue] = dateEntry;
          
          batch.update(cityBreakdownRef, {
            expenses: updatedExpenses,
            earnings: updatedEarnings,
            netEarnings: updatedEarnings - updatedExpenses,
            city: city,
            state: state,
            country: country,
            venueName: venueDetails.venueName,
            formattedAddress: venueDetails.formattedAddress,
            dates: dates,
            lastUpdated: serverTimestamp()
          });
        } else {
          const initialExpenses = recordType === 'expense' ? recordAmount * multiplier : 0;
          const initialEarnings = recordType === 'earning' ? recordAmount * multiplier : 0;
          
          const dates = {};
          dates[dateValue] = {
            expenses: initialExpenses,
            earnings: initialEarnings,
            netEarnings: initialEarnings - initialExpenses
          };
          
          batch.set(cityBreakdownRef, {
            expenses: initialExpenses,
            earnings: initialEarnings,
            netEarnings: initialEarnings - initialExpenses,
            city: city,
            state: state,
            country: country,
            venueName: venueDetails.venueName,
            formattedAddress: venueDetails.formattedAddress,
            dates: dates,
            createdAt: serverTimestamp(),
            lastUpdated: serverTimestamp()
          });
        }
      }
      
      // Commit all the batched writes
      await batch.commit();
    } catch (error) {
      console.error('Error updating financial summary collection:', error);
      throw error;
    }
  };

  // Update settlement summaries with new structure
  const updateSettlementSummaries = async (db, userId, eventId, eventName, dateValue, venueDetails, settledAmount) => {
    try {
      // Update financial summary collection for settlement
      await updateFinancialSummaryCollection(
        db, userId, eventId, eventName, dateValue, venueDetails, 
        'expense', 0, 1, null, true, settledAmount
      );
    } catch (error) {
      console.error('Error updating settlement summaries:', error);
      throw error;
    }
  };

  // Function to fetch financial history from the subcollection structure
  const fetchFinancialHistory = async (eventId, dateId) => {
    if (!eventId || !dateId) return;
    
    setIsLoadingFinancials(true);
    try {
      const db = getFirestore();
      // Use the correct event to determine type
      const relevantEvent = selectedEventForHistory || selectedEvent;
      const eventType = relevantEvent?.type || 'tour';
      const collectionName = eventType === 'tour' ? 'tours' : 'shows';
      
      // Log the path we're using to help with debugging
      console.log(`Fetching financial records from: ${collectionName}/${eventId}/eventDates/${dateId}/financialRecords`);
      
      // Create a reference to the financialRecords subcollection
      const recordsRef = collection(db, collectionName, eventId, 'eventDates', dateId, 'financialRecords');
      
      // Set up real-time listener
      const unsubscribe = onSnapshot(
        query(recordsRef, orderBy('createdAt', 'desc')),
        (snapshot) => {
          const records = {};
          const auditTrailData = {};
          
          // Group financials by date (day)
          snapshot.docs.forEach(doc => {
            const record = { id: doc.id, ...doc.data() };
            // Ensure Firestore timestamps are converted to date strings for consistency
            const recordDate = record.date || (record.createdAt ? new Date(record.createdAt.toDate()).toISOString().split('T')[0] : new Date().toISOString().split('T')[0]);
            
            if (!records[recordDate]) {
              records[recordDate] = { expenses: [], earnings: [] };
            }
            
            // Store audit trail data
            if (record.auditTrail) {
              auditTrailData[record.id] = record.auditTrail;
            }
            
            // Sort records by type
            if (record.type === 'expense') {
              records[recordDate].expenses.push(record);
            } else if (record.type === 'earning') {
              records[recordDate].earnings.push(record);
            }
          });
          
          console.log("Fetched financial records:", records);
          
          // Update state with grouped records
          setFinancialHistory(records);
          setAuditTrail(auditTrailData);
          
          // Calculate totals as fallback
          calculateFinancialTotals(records);
          setIsLoadingFinancials(false);
        },
        (error) => {
          console.error("Error getting financial records:", error);
          setIsLoadingFinancials(false);
        }
      );
      
      // Return unsubscribe function
      return unsubscribe;
    } catch (error) {
      console.error("Error fetching financial history:", error);
      setIsLoadingFinancials(false);
    }
  };

  // Fetch dates with financials for specific event using the new structure
  const fetchDatesWithFinancials = async (eventId) => {
    if (!eventId || !userData) return [];
    
    try {
      const db = getFirestore();
      
      // Get all dates with financials for this event from the new structure
      const eventBreakdownRef = doc(db, 'financialSummary', userData.userId, 'eventBreakdown', eventId);
      const eventBreakdownDoc = await getDoc(eventBreakdownRef);
      
      if (eventBreakdownDoc.exists()) {
        const eventData = eventBreakdownDoc.data();
        const datesMap = eventData.dates || {};
        
        // Filter dates if showing only user's financials
        if (showOnlyUserFinancials) {
          const userDates = Object.entries(datesMap)
            .filter(([_, data]) => data.expenses > 0 || data.earnings > 0)
            .map(([date]) => date);
            
          return userDates.sort((a, b) => moment(b).diff(moment(a)));
        }
        
        // Otherwise return all dates
        return Object.keys(datesMap).sort((a, b) => moment(b).diff(moment(a)));
      }
      
      // Fallback to the old method using financial history
      const eventType = selectedEvent?.type || 'tour';
      const collectionName = eventType === 'tour' ? 'tours' : 'shows';
      
      // Try to get all dates with financial records for this event
      const datesCollection = collection(db, collectionName, eventId, 'eventDates');
      const datesSnapshot = await getDocs(datesCollection);
      
      const allDatesWithFinancials = [];
      
      // Check each date for financial records
      for (const dateDoc of datesSnapshot.docs) {
        const recordsRef = collection(db, collectionName, eventId, 'eventDates', dateDoc.id, 'financialRecords');
        const recordsSnapshot = await getDocs(recordsRef);
        
        if (!recordsSnapshot.empty) {
          // This date has financial records
          const dateData = dateDoc.data();
          allDatesWithFinancials.push(dateData.date);
        }
      }
      
      // Sort the dates (newest first)
      return allDatesWithFinancials.sort((a, b) => moment(b).diff(moment(a)));
      
    } catch (error) {
      console.error("Error fetching dates with financials:", error);
      return datesWithFinancials; // Fallback to existing sorted dates
    }
  }; 

  // Calculate total expenses, earnings, and net earnings
  const calculateFinancialTotals = (financialData) => {
    if (!financialData || !userData) {
      setTotalExpense(0);
      setTotalRecoverableExpense(0);
      setTotalEarnings(0);
      setNetEarnings(0);
      setTotalAmountOwed(0);
      setDetailedExpenses([]);
      setDetailedEarnings([]);
      setDetailedOwed([]);
      return;
    }
  
    let totalPaid = 0;
    let totalRecoverable = 0;
    let totalEarned = 0;
    let totalOwed = 0;  // New variable to track total amount owed
    let detailedPaid = [];
    let detailedEarned = [];
    let detailedOwedTemp = [];
  
    Object.entries(financialData).forEach(([date, dateRecords]) => {
      // Process expenses - skip deleted expenses in totals
      if (dateRecords?.expenses) {
        dateRecords.expenses
          .filter(expense => !expense.isDeleted)
          .forEach(expense => {
            if (expense.paidBy === userData.userId) {
              const settledAmount = expense.whoOwes?.reduce((sum, ower) => 
                ower.isSettled ? sum + parseFloat(ower.amountOwed) : sum, 0
              ) || 0;
              const effectiveAmount = parseFloat(expense.amount) - settledAmount;
              totalPaid += effectiveAmount;
              
              // Calculate recoverable amount (amount owed by others)
              const recoverableAmount = expense.whoOwes?.reduce((sum, ower) => 
                ower.userId !== userData.userId && !ower.isSettled ? sum + parseFloat(ower.amountOwed) : sum, 0
              ) || 0;
              totalRecoverable += recoverableAmount;
  
              detailedPaid.push({
                id: expense.id,
                date,
                amount: parseFloat(expense.amount),
                effectiveAmount,
                recoverable: recoverableAmount,
                currency: expense.currency,
                note: expense.note,
                whoOwes: expense.whoOwes || [],
                isSettled: expense.whoOwes?.every(ower => ower.isSettled) || false,
                isSelfExpense: expense.isSelfExpense,
                category: expense.category || 'Other',
                type: 'expense',
                isDeleted: expense.isDeleted || false,
                createdBy: expense.createdBy,
                auditTrail: expense.auditTrail || []
              });
            }
  
            // Calculate amount the current user owes to others
            if (!expense.isSelfExpense && expense.paidBy !== userData.userId) {
              const userOwed = expense.whoOwes?.find(user => user.userId === userData.userId);
              if (userOwed && !userOwed.isSettled) {
                totalPaid += parseFloat(userOwed.amountOwed);
                totalOwed += parseFloat(userOwed.amountOwed); // Add to total owed
                detailedOwedTemp.push({
                  id: expense.id,
                  date,
                  amount: parseFloat(userOwed.amountOwed),
                  currency: expense.currency,
                  paidBy: allUsers.find(u => u.userId === expense.paidBy)?.fullName || 'Unknown',
                  note: expense.note,
                  category: expense.category || 'Other',
                  type: 'expense',
                  isDeleted: expense.isDeleted || false
                });
              }
            }
        });
      }
  
      // Process earnings - skip deleted earnings in totals
      if (dateRecords?.earnings) {
        dateRecords.earnings
          .filter(earning => !earning.isDeleted)
          .forEach(earning => {
            if (earning.earnedBy === userData.userId) {
              const earnedAmount = parseFloat(earning.amount);
              totalEarned += earnedAmount;
  
              detailedEarned.push({
                id: earning.id,
                date,
                amount: earnedAmount,
                currency: earning.currency,
                note: earning.note,
                source: earning.source,
                category: earning.category || 'Other',
                type: 'earning',
                isDeleted: earning.isDeleted || false,
                createdBy: earning.createdBy,
                recordedBy: earning.recordedBy,
                auditTrail: earning.auditTrail || [],
                isSelfRecorded: earning.recordedBy === earning.earnedBy
              });
            }
        });
      }
    });
  
    setTotalExpense(totalPaid);
    setTotalRecoverableExpense(totalRecoverable);
    setTotalEarnings(totalEarned);
    setNetEarnings(totalEarned - totalPaid);
    setTotalAmountOwed(totalOwed);
    setDetailedExpenses(detailedPaid.sort((a, b) => new Date(b.date) - new Date(a.date)));
    setDetailedEarnings(detailedEarned.sort((a, b) => new Date(b.date) - new Date(a.date)));
    setDetailedOwed(detailedOwedTemp.sort((a, b) => new Date(b.date) - new Date(a.date)));
  };
  
  // Send notification to user about earnings recorded for them
  const sendEarningNotification = async (earnedByUserId, amount, currency, recordedByUser, earningSource, earningNote, venueName, eventName, dateValue) => {
    try {
      // Format the date for better readability
      const formattedDate = moment(dateValue).format('DD-MM-YYYY');
      
      // Create notification data
      const notificationData = {
        type: 'EARNING_RECORDED',
        title: 'New Earning Recorded',
        message: `${recordedByUser.fullName} recorded ${currency} ${amount} for you from ${earningSource || 'event'} at ${venueName} on ${formattedDate}`,
        data: {
          amount,
          currency,
          source: earningSource,
          note: earningNote,
          venueName,
          eventName,
          date: dateValue,
          recordedBy: recordedByUser.userId,
          _displayData: {
            recordedByName: recordedByUser.fullName,
            recordedByPhoto: recordedByUser.profilePictureUrl,
            venueName,
            eventName,
            date: formattedDate,
            amount: `${currency} ${amount}`
          }
        }
      };
      
      // Create the notification
      await createNotification(earnedByUserId, 'general', notificationData, recordedByUser.userId);
      
      return true;
    } catch (error) {
      console.error('Error sending earning notification:', error);
      return false;
    }
  };

  // Send notification to users who owe money
  const sendExpenseOwedNotification = async (owerId, amount, payer, currency, note, category, venueName, eventName, dateValue, expenseId) => {
    try {
      // Format the date for better readability
      const formattedDate = moment(dateValue).format('DD-MM-YYYY');
      
      // Create notification data
      const notificationData = {
        type: 'EXPENSE_OWED',
        title: 'New Expense Recorded',
        message: `You owe ${currency} ${amount} to ${payer.fullName} for ${category} at ${venueName} on ${formattedDate}`,
        data: {
          expenseId,
          amount,
          currency,
          payerId: payer.userId,
          note,
          category,
          venueName,
          eventName,
          date: dateValue,
          eventType: selectedEventForRecord.type || 'tour',
          eventId: selectedEventForRecord.id,
          _displayData: {
            payerName: payer.fullName,
            payerPhoto: payer.profilePictureUrl,
            venueName,
            eventName,
            date: formattedDate,
            amount: `${currency} ${amount}`,
            category
          }
        }
      };
      
      // Create the notification
      await createNotification(owerId, 'general', notificationData, payer.userId);
      
      return true;
    } catch (error) {
      console.error('Error sending expense owed notification:', error);
      return false;
    }
  };

  // Send notification when an expense is settled
  const sendExpenseSettledNotification = async (userId, amount, settledBy, currency, note, category, venueName, eventName, dateValue, expenseId) => {
    try {
      // Format the date for better readability
      const formattedDate = moment(dateValue).format('DD-MM-YYYY');
      
      // Create notification data
      const notificationData = {
        type: 'EXPENSE_SETTLED',
        title: 'Expense Settled',
        message: `${settledBy.fullName} has marked your expense of ${currency} ${amount} for ${category} as settled`,
        data: {
          expenseId,
          amount,
          currency,
          settledById: settledBy.userId,
          note,
          category,
          venueName,
          eventName,
          date: dateValue,
          eventType: selectedEventForHistory?.type || 'tour',
          eventId: selectedEventForHistory?.id,
          _displayData: {
            settledByName: settledBy.fullName,
            settledByPhoto: settledBy.profilePictureUrl,
            venueName,
            eventName, 
            date: formattedDate,
            amount: `${currency} ${amount}`,
            category
          }
        }
      };
      
      // Create the notification
      await createNotification(userId, 'general', notificationData, settledBy.userId);
      
      return true;
    } catch (error) {
      console.error('Error sending expense settled notification:', error);
      return false;
    }
  };

  // Handle financial record submission - updated for both expenses and earnings
  const handleSubmit = async () => {
    // Common validations
    if (!selectedEventForRecord) {
      alert('Please select an event');
      return;
    }
    
    if (!selectedDateValue) {
      alert('Please select a date');
      return;
    }
    
    if (!userData || !selectedEventForRecord) {
      alert('User or event data is missing');
      return;
    }

    // Expense-specific validations
    if (activeRecordTab === 'expense') {
      if (!amount) {
        alert('Please enter an amount');
        return;
      }

      // Validate expense type - either self expense or split with others
      if (!isSelfExpense && (whoOwes.length === 0 || (whoOwes.length === 1 && whoOwes[0].userId === userData.userId))) {
        alert('Please mark as self expense or split with other users');
        return;
      }
    } 
    // Earning-specific validations
    else {
      if (!earningAmount) {
        alert('Please enter an amount');
        return;
      }
      
      // If not self earning, validate user selection
      if (!isSelfEarning) {
        if (!hasFinancialPermission) {
          alert('You do not have permission to record earnings for other users');
          return;
        }
        
        if (!earningForUserId) {
          alert('Please select a user to record earnings for');
          return;
        }
      }
    }

    setIsSubmitting(true);
    try {
      const db = getFirestore();
      const eventType = selectedEventForRecord.type || 'tour';
      const collectionName = eventType === 'tour' ? 'tours' : 'shows';
      
      // Find the date document ID based on the selected date value
      let dateDocId = selectedDate;
      if (!dateDocId && selectedEventForRecord?.eventDates) {
        const selectedDateDoc = selectedEventForRecord.eventDates.find(date => date.date === selectedDateValue);
        if (selectedDateDoc) {
          dateDocId = selectedDateDoc.id;
        } else {
          throw new Error('Could not find the selected date document');
        }
      }
      
      console.log(`Submitting ${activeRecordTab} with dateDocId:`, dateDocId);
      
      // Get selected date object
      const selectedDateObj = selectedEventForRecord.eventDates?.find(date => date.id === dateDocId);
      
      // Get venue details for city-based tracking
      const venueDetails = getVenueDetails(selectedDateObj);
      
      if (activeRecordTab === 'expense') {
        // Create expense data object
        const expenseData = {
          type: 'expense',
          amount: parseFloat(amount),
          date: selectedDateValue,
          paidBy: userData.userId,
          note,
          currency,
          createdBy: userData.userId,
          createdAt: serverTimestamp(),
          isSelfExpense,
          category: filterCategory || 'Other',
          city: venueDetails.city,
          state: venueDetails.state,
          country: venueDetails.country,
          venueName: venueDetails.venueName,
          venueAddress: venueDetails.formattedAddress,
          venueData: selectedDateObj?.venueData || selectedDateObj?.event?.venueDetails?.venueData || {},
          venueDetails: selectedDateObj?.venueDetails || selectedDateObj?.event?.venueDetails || {},
          whoOwes: isSelfExpense ? [] : whoOwes.map(user => ({
            userId: user.userId,
            fullName: user.fullName,
            amountOwed: parseFloat(user.amountOwed),
            isSettled: false
          })),
          isDeleted: false,
          auditTrail: [{
            action: 'created',
            date: new Date().toISOString(),
            by: userData.userId,
            byName: userData.fullName,
            amount: parseFloat(amount),
            note
          }]
        };

        // Add to financialRecords subcollection within the selected date
        const recordsRef = collection(db, collectionName, selectedEventForRecord.id, 'eventDates', dateDocId, 'financialRecords');
        const recordDocRef = await addDoc(recordsRef, expenseData);
        console.log("Added expense document:", recordDocRef.id);
        
        // Update financial summaries using the new collection structure
        await updateFinancialSummaryCollection(
          db, userData.userId, selectedEventForRecord.id, 
          selectedEventForRecord.tourName || selectedEventForRecord.showName,
          selectedDateValue, venueDetails, 'expense', parseFloat(amount), 1,
          expenseData
        );
        
        // If this is a split expense, update summaries for other users too
        if (!isSelfExpense && whoOwes.length > 0) {
          for (const ower of whoOwes) {
            if (ower.userId !== userData.userId) {
              // This is an expense for the person who owes
              await updateFinancialSummaryCollection(
                db, ower.userId, selectedEventForRecord.id,
                selectedEventForRecord.tourName || selectedEventForRecord.showName,
                selectedDateValue, venueDetails, 'expense', parseFloat(ower.amountOwed), 1,
                expenseData
              );

              // NEW: Send notification to users who owe money
              await sendExpenseOwedNotification(
                ower.userId, 
                ower.amountOwed, 
                userData, 
                currency, 
                note, 
                filterCategory, 
                venueDetails.venueName, 
                selectedEventForRecord.tourName || selectedEventForRecord.showName, 
                selectedDateValue,
                recordDocRef.id
              );
            }
          }
        }
      } else {
        // For earnings, determine the user earning the money
        const earnedByUserId = isSelfEarning ? userData.userId : earningForUserId;
        const earnedByUser = isSelfEarning 
          ? { userId: userData.userId, fullName: userData.fullName }
          : allUsers.find(user => user.userId === earningForUserId);
          
        // Create earning data object
        const earningData = {
          type: 'earning',
          amount: parseFloat(earningAmount),
          date: selectedDateValue,
          earnedBy: earnedByUserId,
          earnedByName: earnedByUser?.fullName || 'Unknown User',
          recordedBy: userData.userId,
          recordedByName: userData.fullName,
          source: earningSource,
          note: earningNote,
          currency,
          createdBy: userData.userId,
          createdAt: serverTimestamp(),
          category: earningCategory,
          city: venueDetails.city,
          state: venueDetails.state,
          country: venueDetails.country,
          venueName: venueDetails.venueName,
          venueAddress: venueDetails.formattedAddress,
          venueData: selectedDateObj?.venueData || selectedDateObj?.event?.venueDetails?.venueData || {},
          venueDetails: selectedDateObj?.venueDetails || selectedDateObj?.event?.venueDetails || {},
          isSelfRecorded: isSelfEarning,
          isDeleted: false,
          auditTrail: [{
            action: 'created',
            date: new Date().toISOString(),
            by: userData.userId,
            byName: userData.fullName,
            amount: parseFloat(earningAmount),
            note: earningNote
          }]
        };

        // Add to financialRecords subcollection
        const recordsRef = collection(db, collectionName, selectedEventForRecord.id, 'eventDates', dateDocId, 'financialRecords');
        const recordDocRef = await addDoc(recordsRef, earningData);
        console.log("Added earning document:", recordDocRef.id);
        
        // Update financial summaries using the new collection structure
        await updateFinancialSummaryCollection(
          db, earnedByUserId, selectedEventForRecord.id, 
          selectedEventForRecord.tourName || selectedEventForRecord.showName,
          selectedDateValue, venueDetails, 'earning', parseFloat(earningAmount), 1,
          earningData
        );

        // NEW: Send notification if earnings recorded for another user
        if (!isSelfEarning && earnedByUserId !== userData.userId) {
          await sendEarningNotification(
            earnedByUserId,
            earningAmount, 
            currency,
            userData,
            earningSource,
            earningNote,
            venueDetails.venueName,
            selectedEventForRecord.tourName || selectedEventForRecord.showName,
            selectedDateValue
          );
        }
      }

      // Refresh the financial history to show the new record
      fetchFinancialHistory(selectedEventForRecord.id, dateDocId);
      fetchGlobalFinancialSummary();
      
      alert(`${activeRecordTab.charAt(0).toUpperCase() + activeRecordTab.slice(1)} recorded successfully`);
      resetForm();
      setActiveTab('history');
      setSelectedHistoryDate(selectedDateValue);
      setSelectedEventForHistory(selectedEventForRecord);
    } catch (error) {
      console.error(`Error submitting ${activeRecordTab}:`, error);
      alert(`Failed to record ${activeRecordTab}: ` + error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle settling of expenses
  const handleSettle = async (expenseId, userId) => {
    if (!userData) return;
    
    // Get the relevant event
    const relevantEvent = selectedEventForHistory || selectedEvent;
    if (!relevantEvent) return;
    
    // Find the correct document ID for the date
    let relevantDateId;
    if (selectedHistoryDate) {
      // We're in history view, need to find the document ID for this date string
      relevantDateId = findDateDocId(relevantEvent, selectedHistoryDate);
    } else {
      // We're in record view, already have the document ID
      relevantDateId = selectedDate;
    }
    
    if (!relevantDateId) {
      console.error('Could not find document ID for date:', selectedHistoryDate || selectedDate);
      alert('Error: Could not find the date document');
      return;
    }
    
    setIsSettling(true);
    try {
      const db = getFirestore();
      const eventType = relevantEvent.type || 'tour';
      const collectionName = eventType === 'tour' ? 'tours' : 'shows';
      
      console.log(`Settling expense at path: ${collectionName}/${relevantEvent.id}/eventDates/${relevantDateId}/financialRecords/${expenseId}`);
      
      // Find the expense document in the financialRecords subcollection
      const recordsRef = collection(db, collectionName, relevantEvent.id, 'eventDates', relevantDateId, 'financialRecords');
      const expenseDocRef = doc(recordsRef, expenseId);
      const expenseDoc = await getDoc(expenseDocRef);
      
      if (expenseDoc.exists()) {
        const expenseData = expenseDoc.data();
        
        // Find the user who is settling
        const settlingUser = expenseData.whoOwes.find(ower => ower.userId === userId);
        if (!settlingUser) {
          throw new Error('User not found in expense owed list');
        }
        
        const settledAmount = parseFloat(settlingUser.amountOwed);
        
        // Update the specific user's settled status in whoOwes array
        const updatedWhoOwes = expenseData.whoOwes.map(ower => 
          ower.userId === userId ? {...ower, isSettled: true} : ower
        );
        
        // Add to audit trail
        const auditTrail = expenseData.auditTrail || [];
        auditTrail.push({
          action: 'settled',
          date: new Date().toISOString(),
          by: userData.userId,
          byName: userData.fullName,
          settledFor: userId,
          settledForName: settlingUser.fullName,
          amount: settledAmount
        });
        
        // Update the document
        await updateDoc(expenseDocRef, { 
          whoOwes: updatedWhoOwes,
          auditTrail: auditTrail
        });
        
        // Get venue details
        const selectedDateObj = relevantEvent.eventDates?.find(date => date.id === relevantDateId);
        const venueDetails = getVenueDetails(selectedDateObj);
        
        // Update financial summaries for settlement
        await updateSettlementSummaries(
          db, expenseData.paidBy, relevantEvent.id,
          relevantEvent.tourName || relevantEvent.showName,
          expenseData.date, venueDetails, settledAmount
        );
        
        // Update financial summaries for the settling user
        await updateSettlementSummaries(
          db, userId, relevantEvent.id,
          relevantEvent.tourName || relevantEvent.showName,
          expenseData.date, venueDetails, settledAmount
        );

        // NEW: Send notification to the expense creator that expense has been settled
        await sendExpenseSettledNotification(
          expenseData.paidBy,
          settledAmount,
          userData,
          expenseData.currency,
          expenseData.note,
          expenseData.category,
          venueDetails.venueName,
          relevantEvent.tourName || relevantEvent.showName,
          expenseData.date,
          expenseId
        );
        
        alert('Expense settled successfully');
        
        // Refresh financial summaries
        fetchGlobalFinancialSummary();
      }
    } catch (error) {
      console.error('Error settling expense:', error);
      alert('Failed to settle expense. Please try again.');
    } finally {
      setIsSettling(false);
    }
  };

  // Helper function to find the actual document ID for a given date string
  const findDateDocId = (event, dateString) => {
    if (!event || !event.eventDates || !dateString) return null;
    
    // Find the date document that matches the selected date string
    const dateDoc = event.eventDates.find(date => date.date === dateString);
    return dateDoc ? dateDoc.id : null;
  };

  // Handle editing records
  const handleEditRecord = async (recordId, recordType) => {
    if (!userData) return;
    
    // Get the relevant event 
    const relevantEvent = selectedEventForHistory || selectedEvent;
    if (!relevantEvent) return;
    
    // Find the correct document ID for the date
    let relevantDateId;
    if (selectedHistoryDate) {
      // We're in history view, need to find the document ID for this date string
      relevantDateId = findDateDocId(relevantEvent, selectedHistoryDate);
    } else {
      // We're in record view, already have the document ID
      relevantDateId = selectedDate;
    }
    
    if (!relevantDateId) {
      console.error('Could not find document ID for date:', selectedHistoryDate || selectedDate);
      alert('Error: Could not find the date document');
      return;
    }
    
    // If not in editing mode yet, enter edit mode and load current values
    if (isEditing !== recordId) {
      // Set editing mode for this record
      setIsEditing(recordId);
      
      // Find the record to pre-fill the editing fields
      if (recordType === 'expense') {
        const expense = financialHistory[selectedHistoryDate]?.expenses.find(e => e.id === recordId);
        if (expense) {
          setEditedAmount(expense.amount.toString());
          setEditedNote(expense.note || '');
        }
      } else {
        const earning = financialHistory[selectedHistoryDate]?.earnings.find(e => e.id === recordId);
        if (earning) {
          setEditedAmount(earning.amount.toString());
          setEditedNote(earning.note || '');
        }
      }
      return;
    }
    
    // Validate inputs
    if (!editedAmount || isNaN(parseFloat(editedAmount)) || parseFloat(editedAmount) <= 0) {
      alert('Please enter a valid amount');
      return;
    }
    
    try {
      setIsSubmitting(true);
      
      const db = getFirestore();
      const eventType = relevantEvent.type || 'tour';
      const collectionName = eventType === 'tour' ? 'tours' : 'shows';
      
      console.log(`Updating ${recordType} at path: ${collectionName}/${relevantEvent.id}/eventDates/${relevantDateId}/financialRecords/${recordId}`);
      
      // Reference to the record
      const recordRef = doc(db, collectionName, relevantEvent.id, 'eventDates', relevantDateId, 'financialRecords', recordId);
      const recordDoc = await getDoc(recordRef);
      
      if (!recordDoc.exists()) {
        throw new Error(`${recordType} not found`);
      }
      
      const recordData = recordDoc.data();
      
      // Check permission - only creator or the user who earned/paid can edit
      if (recordType === 'expense' && recordData.paidBy !== userData.userId && recordData.createdBy !== userData.userId) {
        throw new Error('You do not have permission to edit this expense');
      }
      
      if (recordType === 'earning' && recordData.earnedBy !== userData.userId && recordData.createdBy !== userData.userId && !hasFinancialPermission) {
        throw new Error('You do not have permission to edit this earning');
      }
      
      // Build audit trail entry
      const auditTrail = recordData.auditTrail || [];
      auditTrail.push({
        action: 'edited',
        date: new Date().toISOString(),
        by: userData.userId,
        byName: userData.fullName,
        previousAmount: parseFloat(recordData.amount),
        newAmount: parseFloat(editedAmount),
        previousNote: recordData.note,
        newNote: editedNote
      });
      
      // Calculate difference for summary updates
      const amountDifference = parseFloat(editedAmount) - parseFloat(recordData.amount);
      
      // Update the record
      await updateDoc(recordRef, {
        amount: parseFloat(editedAmount),
        note: editedNote,
        auditTrail: auditTrail,
        lastUpdated: serverTimestamp(),
        lastUpdatedBy: userData.userId,
        lastUpdatedByName: userData.fullName
      });
      
      // Update financial summaries if amount has changed
      if (amountDifference !== 0) {
        const selectedDateObj = relevantEvent.eventDates?.find(date => date.id === relevantDateId);
        const venueDetails = getVenueDetails(selectedDateObj);
        
        if (recordType === 'expense') {
          // Update summary for the expense payer
          await updateFinancialSummaryCollection(
            db, recordData.paidBy, relevantEvent.id,
            relevantEvent.tourName || relevantEvent.showName,
            recordData.date, venueDetails, 'expense', Math.abs(amountDifference), 
            amountDifference > 0 ? 1 : -1, recordData
          );
          
          // Update for others who owe money if not a self expense
          if (!recordData.isSelfExpense && recordData.whoOwes) {
            // Calculate new split amounts based on percentage of original
            const totalOriginal = parseFloat(recordData.amount);
            const newAmount = parseFloat(editedAmount);
            
            for (const ower of recordData.whoOwes) {
              if (ower.userId !== recordData.paidBy) {
                // Calculate percentage this person owes of the original
                const percentage = parseFloat(ower.amountOwed) / totalOriginal;
                // Apply percentage to new amount
                const newOwedAmount = percentage * newAmount;
                const owedDifference = newOwedAmount - parseFloat(ower.amountOwed);
                
                if (owedDifference !== 0) {
                  await updateFinancialSummaryCollection(
                    db, ower.userId, relevantEvent.id,
                    relevantEvent.tourName || relevantEvent.showName,
                    recordData.date, venueDetails, 'expense', Math.abs(owedDifference),
                    owedDifference > 0 ? 1 : -1, recordData
                  );
                }
              }
            }
          }
        } else {
          // Update summary for earning
          await updateFinancialSummaryCollection(
            db, recordData.earnedBy, relevantEvent.id,
            relevantEvent.tourName || relevantEvent.showName,
            recordData.date, venueDetails, 'earning', Math.abs(amountDifference),
            amountDifference > 0 ? 1 : -1, recordData
          );
        }
      }
      
      // Reset edit state
      setIsEditing(null);
      setEditedAmount('');
      setEditedNote('');
      
      // Refresh data
      fetchFinancialHistory(relevantEvent.id, relevantDateId);
      fetchGlobalFinancialSummary();
      
      alert(`${recordType.charAt(0).toUpperCase() + recordType.slice(1)} updated successfully`);
    } catch (error) {
      console.error(`Error updating ${recordType}:`, error);
      alert(`Failed to update ${recordType}: ` + error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Delete financial records (soft delete)
  const handleDeleteFinancialRecord = async (recordId, recordType) => {
    if (!userData) return;
    
    // Get the relevant event
    const relevantEvent = selectedEventForHistory || selectedEvent;
    if (!relevantEvent) return;
    
    // Find the correct document ID for the date
    let relevantDateId;
    if (selectedHistoryDate) {
      // We're in history view, need to find the document ID for this date string
      relevantDateId = findDateDocId(relevantEvent, selectedHistoryDate);
    } else {
      // We're in record view, already have the document ID
      relevantDateId = selectedDate;
    }
    
    if (!relevantDateId) {
      console.error('Could not find document ID for date:', selectedHistoryDate || selectedDate);
      alert('Error: Could not find the date document');
      return;
    }
    
    if (!window.confirm(`Are you sure you want to delete this ${recordType}? It will be marked as deleted but still visible in the history.`)) {
      return;
    }

    try {
      const db = getFirestore();
      const eventType = relevantEvent.type || 'tour';
      const collectionName = eventType === 'tour' ? 'tours' : 'shows';
      
      console.log(`Deleting ${recordType} at path: ${collectionName}/${relevantEvent.id}/eventDates/${relevantDateId}/financialRecords/${recordId}`);
      
      // Get the record data before soft-deleting
      const recordDocRef = doc(db, collectionName, relevantEvent.id, 'eventDates', relevantDateId, 'financialRecords', recordId);
      const recordDoc = await getDoc(recordDocRef);
      
      if (recordDoc.exists()) {
        const recordData = recordDoc.data();
        
        // Check permission - only creator or the user who earned/paid can delete
        if (recordType === 'expense' && recordData.paidBy !== userData.userId && recordData.createdBy !== userData.userId) {
          throw new Error('You do not have permission to delete this expense');
        }
        
        if (recordType === 'earning' && recordData.earnedBy !== userData.userId && recordData.createdBy !== userData.userId && !hasFinancialPermission) {
          throw new Error('You do not have permission to delete this earning');
        }
        
        // Build audit trail entry for deletion
        const auditTrail = recordData.auditTrail || [];
        auditTrail.push({
          action: 'deleted',
          date: new Date().toISOString(),
          by: userData.userId,
          byName: userData.fullName,
          amount: parseFloat(recordData.amount)
        });
        
        // Soft delete by marking as deleted
        await updateDoc(recordDocRef, {
          isDeleted: true,
          auditTrail: auditTrail,
          deletedAt: serverTimestamp(),
          deletedBy: userData.userId,
          deletedByName: userData.fullName
        });
        
        // Get venue details
        const selectedDateObj = relevantEvent.eventDates?.find(date => date.id === relevantDateId);
        const venueDetails = {
          city: recordData.city || selectedDateObj?.venue?.city || 'Unknown',
          state: recordData.state || selectedDateObj?.venue?.state || '',
          country: recordData.country || selectedDateObj?.venue?.country || '',
          venueName: recordData.venueName || selectedDateObj?.venue?.venueName || '',
          formattedAddress: recordData.venueAddress || selectedDateObj?.venue?.formattedAddress || ''
        };
        
        // Update financial summaries using the new collection structure with negative multiplier
        // This effectively removes the amount from totals
        await updateFinancialSummaryCollection(
          db, recordType === 'expense' ? recordData.paidBy : recordData.earnedBy, 
          relevantEvent.id, 
          relevantEvent.tourName || relevantEvent.showName,
          recordData.date, venueDetails, recordData.type, parseFloat(recordData.amount), -1,
          recordData
        );
        
        // If this is a split expense, update summaries for other users too
        if (recordData.type === 'expense' && !recordData.isSelfExpense && recordData.whoOwes) {
          for (const ower of recordData.whoOwes) {
            if (ower.userId !== recordData.paidBy) {
              // This was an expense for the person who owed, so remove it
              await updateFinancialSummaryCollection(
                db, ower.userId, relevantEvent.id,
                relevantEvent.tourName || relevantEvent.showName,
                recordData.date, venueDetails, 'expense', parseFloat(ower.amountOwed), -1,
                recordData
              );
            }
          }
        }
        
        // Refresh financial summaries
        fetchGlobalFinancialSummary();
        
        alert(`${recordType.charAt(0).toUpperCase() + recordType.slice(1)} deleted successfully`);
      }
    } catch (error) {
      console.error(`Error deleting ${recordType}:`, error);
      alert(`Failed to delete ${recordType}: ` + error.message);
    }
  };

  // Fetch analytics data for charts and insights
  const fetchAnalyticsData = async () => {
    if (!userData?.userId) return;
    
    setIsLoadingAnalytics(true);
    try {
      const db = getFirestore();
      const analyticsResults = {
        byDate: [],
        byMonth: [],
        byYear: [],
        byEvent: [],
        byCity: []
      };
      
      // 1. Get date data from dateBreakdown subcollection
      const dateBreakdownRef = collection(db, 'financialSummary', userData.userId, 'dateBreakdown');
      const dateBreakdownSnapshot = await getDocs(dateBreakdownRef);
      
      if (!dateBreakdownSnapshot.empty) {
        const datesData = dateBreakdownSnapshot.docs.map(doc => {
          const data = doc.data();
          return {
            date: data.date,
            eventId: data.eventId,
            eventName: data.eventName || 'Unknown Event',
            expenses: data.expenses || 0,
            earnings: data.earnings || 0,
            net: (data.earnings || 0) - (data.expenses || 0),
            venueName: data.venueName || 'Unknown Venue',
            formattedAddress: data.formattedAddress || '',
            city: data.city || '',
            state: data.state || '',
            country: data.country || ''
          };
        });
        
        // Sort by date descending for charts
        analyticsResults.byDate = datesData.sort((a, b) => moment(b.date).diff(moment(a.date)));
      }
      
      // 2. Get monthly data from monthBreakdown subcollection
      const monthBreakdownRef = collection(db, 'financialSummary', userData.userId, 'monthBreakdown');
      const monthBreakdownSnapshot = await getDocs(monthBreakdownRef);
      
      if (!monthBreakdownSnapshot.empty) {
        const monthsData = monthBreakdownSnapshot.docs.map(doc => {
          const data = doc.data();
          const month_year = doc.id; // e.g., "july_2025"
          const [month, year] = month_year.split('_');
          
          return {
            id: month_year,
            month: month,
            year: year,
            name: month.charAt(0).toUpperCase() + month.slice(1) + ' ' + year,
            expenses: data.expenses || 0,
            earnings: data.earnings || 0,
            net: (data.earnings || 0) - (data.expenses || 0)
          };
        });
        
        // Sort by date ascending for charts
        analyticsResults.byMonth = monthsData.sort((a, b) => {
          const dateA = new Date(`${a.month} 1, ${a.year}`);
          const dateB = new Date(`${b.month} 1, ${b.year}`);
          return dateA - dateB;
        });
      }
      
      // 3. Get yearly data from yearBreakdown subcollection
      const yearBreakdownRef = collection(db, 'financialSummary', userData.userId, 'yearBreakdown');
      const yearBreakdownSnapshot = await getDocs(yearBreakdownRef);
      
      if (!yearBreakdownSnapshot.empty) {
        const yearsData = yearBreakdownSnapshot.docs.map(doc => {
          const data = doc.data();
          return {
            year: doc.id,
            name: doc.id,
            expenses: data.expenses || 0,
            earnings: data.earnings || 0,
            net: (data.earnings || 0) - (data.expenses || 0),
            months: data.months || {}
          };
        });
        
        // Sort by year ascending for charts
        analyticsResults.byYear = yearsData.sort((a, b) => a.year - b.year);
      }
      
      // 4. Get event data from eventBreakdown subcollection
      const eventBreakdownRef = collection(db, 'financialSummary', userData.userId, 'eventBreakdown');
      const eventBreakdownSnapshot = await getDocs(eventBreakdownRef);
      
      if (!eventBreakdownSnapshot.empty) {
        const eventsData = eventBreakdownSnapshot.docs.map(doc => {
          const data = doc.data();
          return {
            id: doc.id,
            name: data.eventName || 'Unknown Event',
            expenses: data.expenses || 0,
            earnings: data.earnings || 0,
            net: (data.earnings || 0) - (data.expenses || 0),
            dates: data.dates || {}
          };
        });
        
        analyticsResults.byEvent = eventsData;
      }
      
      // 5. Get city data from cityBreakdown subcollection
      const cityBreakdownRef = collection(db, 'financialSummary', userData.userId, 'cityBreakdown');
      const cityBreakdownSnapshot = await getDocs(cityBreakdownRef);
      
      if (!cityBreakdownSnapshot.empty) {
        const citiesData = cityBreakdownSnapshot.docs.map(doc => {
          const data = doc.data();
          const city_state_country = doc.id; // e.g., "mumbai_maharashtra_india"
          const [city, state, country] = city_state_country.split('_');
          
          const displayName = [
            city.charAt(0).toUpperCase() + city.slice(1),
            state ? state.charAt(0).toUpperCase() + state.slice(1) : '',
            country ? country.charAt(0).toUpperCase() + country.slice(1) : ''
          ].filter(Boolean).join(', ');
          
          return {
            id: city_state_country,
            city: city,
            state: state,
            country: country,
            name: displayName,
            venueName: data.venueName || 'Unknown Venue',
            formattedAddress: data.formattedAddress || '',
            expenses: data.expenses || 0,
            earnings: data.earnings || 0,
            net: (data.earnings || 0) - (data.expenses || 0),
            dates: data.dates || {}
          };
        });
        
        analyticsResults.byCity = citiesData;
      }
      
      setAnalyticsData(analyticsResults);
      setIsLoadingAnalytics(false);
    } catch (error) {
      console.error('Error fetching analytics data:', error);
      setIsLoadingAnalytics(false);
    }
  };

  // Get event date details by event
  const getEventDateDetails = (eventId) => {
    if (analyticsData.byEvent.length === 0) return [];
    
    const eventData = analyticsData.byEvent.find(event => event.id === eventId);
    if (!eventData || !eventData.dates) return [];
    
    return Object.entries(eventData.dates).map(([date, data]) => ({
      date,
      expenses: data.expenses || 0,
      earnings: data.earnings || 0,
      net: (data.earnings || 0) - (data.expenses || 0)
    })).sort((a, b) => moment(b.date).diff(moment(a.date)));
  };

  // Generate expense report for selected dates or events
  const generateExcelReport = async () => {
    if (!userData) return;
    
    try {
      const workbook = XLSX.utils.book_new();
      
      if (reportType === 'dates') {
        // Report by selected dates
        const sortedSelectedDates = selectedDatesForReport.sort((a, b) => moment(b).diff(moment(a)));
        
        for (const date of sortedSelectedDates) {
          await generateDateWorksheet(workbook, date);
        }
        
        // Add summary worksheet
        await generateSummaryWorksheet(workbook, 'Selected Dates');
      } 
      else if (reportType === 'events') {
        // Report by selected events
        for (const eventId of selectedEventsForReport) {
          const event = userEvents.find(e => e.id === eventId);
          if (event) {
            await generateEventWorksheet(workbook, event);
          }
        }
        
        // Add summary worksheet
        await generateSummaryWorksheet(workbook, 'Selected Events');
      }
      else if (reportType === 'year') {
        // Report by year
        const years = analyticsData.byYear.map(y => y.year);
        for (const year of years) {
          await generateYearWorksheet(workbook, year);
        }
        
        // Add summary worksheet
        await generateSummaryWorksheet(workbook, 'All Years');
      }
      
      // Generate Excel file
      const wbout = XLSX.write(workbook, { type: 'array', bookType: 'xlsx' });
      const blob = new Blob([wbout], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Financial_Report_${moment().format('YYYY-MM-DD_HH-mm-ss')}.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error generating report:', error);
      alert('Failed to generate financial report. Please try again.');
    }
  };

  // Generate worksheet for a specific date
  const generateDateWorksheet = async (workbook, date) => {
    if (!financialHistory[date]) return;
    
    const dateRecords = financialHistory[date];
    const dateDisplay = moment(date).format('DD-MM-YYYY');
    
    // Create worksheet data
    const ws_data = [
      ['Date', 'Type', 'Amount', 'Category', 'Description', 'User', 'Details', 'Net Impact', 'Status'],
    ];
    
    // Process expenses
    const expenses = dateRecords.expenses || [];
    expenses.forEach(expense => {
      // Calculate remaining amount excluding self-owed portions
      const amountRemaining = expense.isSelfExpense 
        ? ''
        : expense.whoOwes
            .filter(ower => !ower.isSettled && ower.userId !== expense.paidBy)
            .map(ower => `${ower.fullName}: ${expense.currency} ${ower.amountOwed}`)
            .join(', ');

      ws_data.push([
        dateDisplay,
        'Expense',
        `${expense.currency} ${expense.amount}`,
        expense.category || 'Other',
        expense.note || '',
        allUsers.find(u => u.userId === expense.paidBy)?.fullName || 'Unknown',
        expense.isSelfExpense ? 'Self Expense' : expense.whoOwes.map(ower => 
          `${ower.fullName}: ${expense.currency} ${ower.amountOwed}${ower.isSettled ? ' (Settled)' : ''}`
        ).join(', '),
        amountRemaining,
        expense.isDeleted ? 'Deleted' : 'Active'
      ]);
    });
    
    // Process earnings
    const earnings = dateRecords.earnings || [];
    earnings.forEach(earning => {
      ws_data.push([
        dateDisplay,
        'Earning',
        `${earning.currency} ${earning.amount}`,
        earning.category || 'Other',
        earning.note || '',
        allUsers.find(u => u.userId === earning.earnedBy)?.fullName || 'Unknown',
        earning.source || '',
        `+ ${earning.currency} ${earning.amount}`,
        earning.isDeleted ? 'Deleted' : 'Active'
      ]);
    });
    
    // Add summary for this date - exclude deleted entries
    const activeExpenses = expenses.filter(exp => !exp.isDeleted);
    const activeEarnings = earnings.filter(earn => !earn.isDeleted);
    
    const totalExpenses = activeExpenses.reduce((sum, exp) => sum + parseFloat(exp.amount), 0);
    const totalEarnings = activeEarnings.reduce((sum, earn) => sum + parseFloat(earn.amount), 0);
    
    // Get venue info from the first record
    const venueName = expenses[0]?.venueName || earnings[0]?.venueName || 'Unknown';
    const venueAddress = expenses[0]?.venueAddress || earnings[0]?.venueAddress || '';
    
    ws_data.push(
      [''], // Empty row for spacing
      ['Summary'],
      ['Total Expenses', `₹ ${totalExpenses.toFixed(2)}`],
      ['Total Earnings', `₹ ${totalEarnings.toFixed(2)}`],
      ['Net Earnings', `₹ ${(totalEarnings - totalExpenses).toFixed(2)}`],
      [''],
      ['Event', selectedEventForHistory?.tourName || selectedEventForHistory?.showName || 'Unknown'],
      ['Venue', venueName],
      ['Location', venueAddress]
    );
    
    // Add audit trail data
    if (expenses.some(exp => exp.auditTrail?.length > 0) || earnings.some(earn => earn.auditTrail?.length > 0)) {
      ws_data.push(
        [''],
        ['Audit Trail'],
        ['Record Type', 'Record ID', 'Action', 'Date', 'By', 'Details']
      );
      
      // Add expense audit trails
      expenses.forEach(expense => {
        if (expense.auditTrail?.length > 0) {
          expense.auditTrail.forEach(audit => {
            let details = '';
            if (audit.action === 'created') {
              details = `Created with amount ${expense.currency} ${audit.amount}`;
            } else if (audit.action === 'edited') {
              details = `Changed amount from ${expense.currency} ${audit.previousAmount} to ${expense.currency} ${audit.newAmount}`;
            } else if (audit.action === 'deleted') {
              details = `Deleted amount ${expense.currency} ${audit.amount}`;
            } else if (audit.action === 'settled') {
              details = `Settled amount ${expense.currency} ${audit.amount} for ${audit.settledForName}`;
            }
            ws_data.push([
              'Expense',
              expense.id,
              audit.action,
              moment(audit.date).format('DD-MM-YYYY HH:mm:ss'),
              audit.byName || 'Unknown',
              details
            ]);
          });
        }
      });
      
      // Add earning audit trails
      earnings.forEach(earning => {
        if (earning.auditTrail?.length > 0) {
          earning.auditTrail.forEach(audit => {
            let details = '';
            if (audit.action === 'created') {
              details = `Created with amount ${earning.currency} ${audit.amount}`;
            } else if (audit.action === 'edited') {
              details = `Changed amount from ${earning.currency} ${audit.previousAmount} to ${earning.currency} ${audit.newAmount}`;
            } else if (audit.action === 'deleted') {
              details = `Deleted amount ${earning.currency} ${audit.amount}`;
            }
            
            ws_data.push([
              'Earning',
              earning.id,
              audit.action,
              moment(audit.date).format('DD-MM-YYYY HH:mm:ss'),
              audit.byName || 'Unknown',
              details
            ]);
          });
        }
      });
    }
    
    // Add chart data (as a table, since Excel charts require more complex handling)
    if (activeExpenses.length > 0) {
      ws_data.push(
        [''],
        ['Expense Categories'],
        ['Category', 'Amount']
      );
      
      const expensesByCategory = activeExpenses.reduce((acc, exp) => {
        const category = exp.category || 'Other';
        acc[category] = (acc[category] || 0) + parseFloat(exp.amount);
        return acc;
      }, {});
      
      Object.entries(expensesByCategory).forEach(([category, amount]) => {
        ws_data.push([category, amount]);
      });
    }
    
    if (activeEarnings.length > 0) {
      ws_data.push(
        [''],
        ['Earning Categories'],
        ['Category', 'Amount']
      );
      
      const earningsByCategory = activeEarnings.reduce((acc, earn) => {
        const category = earn.category || 'Other';
        acc[category] = (acc[category] || 0) + parseFloat(earn.amount);
        return acc;
      }, {});
      
      Object.entries(earningsByCategory).forEach(([category, amount]) => {
        ws_data.push([category, amount]);
      });
    }
    
    // Create worksheet
    const ws = XLSX.utils.aoa_to_sheet(ws_data);
    
    // Set column widths
    const colWidths = [
      { wch: 12 }, // Date
      { wch: 10 }, // Type
      { wch: 15 }, // Amount
      { wch: 15 }, // Category
      { wch: 30 }, // Description
      { wch: 20 }, // User
      { wch: 40 }, // Details
      { wch: 20 }, // Net Impact
      { wch: 10 }  // Status
    ];
    ws['!cols'] = colWidths;
    
    // Add worksheet to workbook
    XLSX.utils.book_append_sheet(workbook, ws, dateDisplay);
  };

  // Generate worksheet for a specific event
  const generateEventWorksheet = async (workbook, event) => {
    // Create worksheet data
    const ws_data = [
      ['Event', event.tourName || event.showName || 'Unknown Event'],
      [''],
      ['Date', 'Expenses', 'Earnings', 'Net']
    ];
    
    // Get event data from analytics
    const eventData = analyticsData.byEvent.find(e => e.id === event.id);
    if (eventData && eventData.dates) {
      const dateEntries = Object.entries(eventData.dates)
        .map(([date, data]) => ({
          date,
          expenses: data.expenses || 0,
          earnings: data.earnings || 0,
          net: (data.earnings || 0) - (data.expenses || 0)
        }))
        .sort((a, b) => moment(b.date).diff(moment(a.date)));
      
      // Add date rows
      dateEntries.forEach(entry => {
        ws_data.push([
          moment(entry.date).format('DD-MM-YYYY'),
          entry.expenses.toFixed(2),
          entry.earnings.toFixed(2),
          entry.net.toFixed(2)
        ]);
      });
      
      // Add totals
      const totalExpenses = dateEntries.reduce((sum, entry) => sum + entry.expenses, 0);
      const totalEarnings = dateEntries.reduce((sum, entry) => sum + entry.earnings, 0);
      const netEarnings = totalEarnings - totalExpenses;
      
      ws_data.push(
        [''],
        ['Summary'],
        ['Total Expenses', `₹ ${totalExpenses.toFixed(2)}`],
        ['Total Earnings', `₹ ${totalEarnings.toFixed(2)}`],
        ['Net Earnings', `₹ ${netEarnings.toFixed(2)}`]
      );
      
      // Add chart data
      ws_data.push(
        [''],
        ['Chart Data'],
        ['Date', 'Expenses', 'Earnings', 'Net']
      );
      
      dateEntries.forEach(entry => {
        ws_data.push([
          moment(entry.date).format('DD-MM-YYYY'),
          entry.expenses,
          entry.earnings,
          entry.net
        ]);
      });
    }
    
    // Create worksheet
    const ws = XLSX.utils.aoa_to_sheet(ws_data);
    
    // Set column widths
    const colWidths = [
      { wch: 15 }, // Date
      { wch: 15 }, // Expenses
      { wch: 15 }, // Earnings
      { wch: 15 }  // Net
    ];
    ws['!cols'] = colWidths;
    
    // Add worksheet to workbook
    XLSX.utils.book_append_sheet(workbook, ws, (event.tourName || event.showName || 'Event').substring(0, 30));
  };

  // Generate worksheet for a specific year
  const generateYearWorksheet = async (workbook, year) => {
    // Create worksheet data
    const ws_data = [
      ['Year', year],
      [''],
      ['Month', 'Expenses', 'Earnings', 'Net']
    ];
    
    // Get year data from analytics
    const yearData = analyticsData.byYear.find(y => y.year === year);
    if (yearData) {
      // Get detailed month data from the backend
      const db = getFirestore();
      const yearDocRef = doc(db, 'financialSummary', userData.userId, 'yearBreakdown', year);
      const yearDoc = await getDoc(yearDocRef);
      
      if (yearDoc.exists()) {
        const data = yearDoc.data();
        const months = data.months || {};
        
        // Convert to array and sort by month
        const monthNames = ['january', 'february', 'march', 'april', 'may', 'june', 
                            'july', 'august', 'september', 'october', 'november', 'december'];
        
        const monthEntries = monthNames
          .filter(month => months[month])
          .map(month => ({
            month,
            expenses: months[month].expenses || 0,
            earnings: months[month].earnings || 0,
            net: (months[month].earnings || 0) - (months[month].expenses || 0)
          }));
        
        // Add month rows
        monthEntries.forEach(entry => {
          ws_data.push([
            entry.month.charAt(0).toUpperCase() + entry.month.slice(1),
            entry.expenses.toFixed(2),
            entry.earnings.toFixed(2),
            entry.net.toFixed(2)
          ]);
        });
        
        // Add totals
        ws_data.push(
          [''],
          ['Summary'],
          ['Total Expenses', `₹ ${yearData.expenses.toFixed(2)}`],
          ['Total Earnings', `₹ ${yearData.earnings.toFixed(2)}`],
          ['Net Earnings', `₹ ${yearData.net.toFixed(2)}`]
        );
        
        // Add chart data
        ws_data.push(
          [''],
          ['Chart Data'],
          ['Month', 'Expenses', 'Earnings', 'Net']
        );
        
        monthEntries.forEach(entry => {
          ws_data.push([
            entry.month.charAt(0).toUpperCase() + entry.month.slice(1),
            entry.expenses,
            entry.earnings,
            entry.net
          ]);
        });
      }
    }
    
    // Create worksheet
    const ws = XLSX.utils.aoa_to_sheet(ws_data);
    
    // Set column widths
    const colWidths = [
      { wch: 15 }, // Month
      { wch: 15 }, // Expenses
      { wch: 15 }, // Earnings
      { wch: 15 }  // Net
    ];
    ws['!cols'] = colWidths;
    
    // Add worksheet to workbook
    XLSX.utils.book_append_sheet(workbook, ws, `Year ${year}`);
  };

  // Generate summary worksheet
  const generateSummaryWorksheet = async (workbook, title) => {
    // Create worksheet data
    const ws_data = [
      ['Financial Summary', title],
      ['Generated on', moment().format('DD-MM-YYYY HH:mm:ss')],
      [''],
      ['Global Totals'],
      ['Total Expenses', `₹ ${totalExpense.toFixed(2)}`],
      ['Recoverable Expenses', `₹ ${totalRecoverableExpense.toFixed(2)}`],
      ['Total Earnings', `₹ ${totalEarnings.toFixed(2)}`],
      ['Net Earnings', `₹ ${netEarnings.toFixed(2)}`],
      ['']
    ];
    
    // Add charts section
    ws_data.push(
      ['Charts'],
      ['The report includes data tables that can be used to create charts in Excel:'],
      ['1. In each worksheet, look for the "Chart Data" section'],
      ['2. Select the data and click Insert > Chart in Excel'],
      ['3. Choose the chart type that best represents your data'],
      ['']
    );
    
    // Add report info
    ws_data.push(
      ['Report Information'],
      ['User', userData.fullName],
      ['Email', userData.email],
      ['Membership Tier', userData.membershipTier || 'Standard']
    );
    
    // Create worksheet
    const ws = XLSX.utils.aoa_to_sheet(ws_data);
    
    // Set column widths
    const colWidths = [
      { wch: 20 },
      { wch: 30 }
    ];
    ws['!cols'] = colWidths;
    
    // Add worksheet to workbook
    XLSX.utils.book_append_sheet(workbook, ws, 'Summary');
  };

  const BottomNav = () => (
    <div className={styles['bottom-nav']}>
      <button onClick={() => navigate('/dashboard')} className={styles['nav-item']}>
        <FontAwesomeIcon icon={faHome} />
        <span>Home</span>
      </button>
      <button onClick={() => navigate('/profile')} className={styles['nav-item']}>
        <FontAwesomeIcon icon={faUser} />
        <span>Profile</span>
      </button>
      <button onClick={() => navigate('/membership')} className={styles['nav-item']}>
        <FontAwesomeIcon icon={faWallet} />
        <span>Membership</span>
      </button>
      <button onClick={() => navigate('/settings')} className={styles['nav-item']}>
        <FontAwesomeIcon icon={faCog} />
        <span>Settings</span>
      </button>
    </div>
  );

  // Render audit history for financial records
  const renderAuditHistory = (recordId) => {
    if (!auditTrail[recordId] || auditTrail[recordId].length === 0) {
      return <div className={styles['no-audit']}>No audit history available</div>;
    }
    
    return (
      <div className={styles['audit-trail']}>
        <h4>Audit History</h4>
        {auditTrail[recordId].map((audit, index) => (
          <div key={index} className={styles['audit-item']}>
            <div className={styles['audit-header']}>
              <span className={styles['audit-action']}>
                {audit.action.charAt(0).toUpperCase() + audit.action.slice(1)}
              </span>
              <span className={styles['audit-date']}>
                {moment(audit.date).format('DD-MM-YYYY HH:mm')}
              </span>
              <span className={styles['audit-by']}>
                by {audit.byName || 'Unknown'}
              </span>
            </div>
            <div className={styles['audit-details']}>
              {audit.action === 'created' && (
                <p>Created with amount {currency} {audit.amount.toFixed(2)}</p>
              )}
              {audit.action === 'edited' && (
                <p>Changed amount from {currency} {audit.previousAmount.toFixed(2)} to {currency} {audit.newAmount.toFixed(2)}</p>
              )}
              {audit.action === 'deleted' && (
                <p>Deleted amount {currency} {audit.amount.toFixed(2)}</p>
              )}
              {audit.action === 'settled' && (
                <p>Settled amount {currency} {audit.amount.toFixed(2)} for {audit.settledForName}</p>
              )}
              {audit.previousNote !== audit.newNote && audit.action === 'edited' && (
                <div className={styles['note-change']}>
                  <p>Previous note: {audit.previousNote || '<empty>'}</p>
                  <p>New note: {audit.newNote || '<empty>'}</p>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    );
  };

  // If the user is not logged in, show login prompt
  if (!userData) {
    return (
      <div className={styles.container}>
        <div className={styles['main-layout']}>
          {!isMobile && <LeftMenu />}
          <div className={styles.content}>
            <LoginPrompt />
          </div>
          {!isMobile && <RightMenu />}
        </div>
        {isMobile && <BottomNav />}
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles['main-layout']}>
        {!isMobile && <LeftMenu />}
        <div className={styles.content}>
          <div className={styles['expense-header']}>
            <h1>Financial Management</h1>
            
            <div className={styles['summary-section']}>
              <div className={styles['summary-cards']}>
                {/* Total Expenses Card with clearer breakdown */}
                <div className={styles['summary-card']}>
                  <FontAwesomeIcon icon={faChartLine} className={styles['card-icon']} />
                  <div className={styles['card-content']}>
                    <h3>Total Expenses</h3>
                    <p className={styles.amount}>
                      {currency} {totalExpense.toFixed(2)}
                    </p>
                  
                      <div className={styles['amount-breakdown']}>
                        <span className={styles['breakdown-item']}>
                          Personal: {currency} {(totalExpense - totalRecoverableExpense).toFixed(2)}
                        </span>
                         <span className={styles['breakdown-item']}>
      Recoverable: {currency} {totalRecoverableExpense.toFixed(2)}
    </span>
                        <span className={styles['breakdown-item']}>
                          After Settlement: {currency} {(totalExpense - totalRecoverableExpense).toFixed(2)}
                        </span>
                      </div>
                  
                  </div>
                </div>
                
                {/* Total Earnings Card */}
                <div className={styles['summary-card']}>
                  <FontAwesomeIcon icon={faCoins} className={styles['card-icon']} style={{color: '#4caf50'}} />
                  <div className={styles['card-content']}>
                    <h3>Total Earnings</h3>
                    <p className={styles.amount} style={{color: '#4caf50'}}>{currency} {totalEarnings.toFixed(2)}</p>
                  </div>
                </div>
                
                {/* Amount Owed Card */}
                <div className={styles['summary-card']}>
                  <FontAwesomeIcon icon={faUserMinus} className={styles['card-icon']} style={{color: '#ff9800'}} />
                  <div className={styles['card-content']}>
                    <h3>Amount Owed</h3>
                    <p className={styles.amount} style={{color: '#ff9800'}}>{currency} {totalAmountOwed.toFixed(2)}</p>
                  </div>
                </div>
                
                {/* Net Earnings Card */}
                <div className={styles['summary-card']}>
                  <FontAwesomeIcon icon={faExchangeAlt} className={styles['card-icon']} style={{color: netEarnings >= 0 ? '#4caf50' : '#f44336'}} />
                  <div className={styles['card-content']}>
                    <h3>Net Earnings</h3>
                    <p className={styles.amount} style={{color: netEarnings >= 0 ? '#4caf50' : '#f44336'}}>
                      {currency} {netEarnings.toFixed(2)}
                    </p>
                  </div>
                </div>
              </div>
              
              {/* Report generation button */}
              {userData?.membershipTier === 'Elite' && (
                <button 
                  className={styles['generate-report-button']}
                  onClick={() => setShowDateSelection(true)}
                >
                  <FontAwesomeIcon icon={faDownload} />
                  Generate Report
                </button>
              )}
            </div>
          </div>
  
          <div className={styles['tab-container']}>
            <div className={styles.tabs}>
              <div 
                className={`${styles.tab} ${activeTab === 'record' ? styles.active : ''}`}
                onClick={() => setActiveTab('record')}
              >
                <FontAwesomeIcon icon={faPlus} className={styles['tab-icon']} />
                Record
              </div>
              <div 
                className={`${styles.tab} ${activeTab === 'history' ? styles.active : ''}`}
                onClick={() => setActiveTab('history')}
              >
                <FontAwesomeIcon icon={faHistory} className={styles['tab-icon']} />
                History
              </div>
              <div 
                className={`${styles.tab} ${activeTab === 'analytics' ? styles.active : ''}`}
                onClick={() => setActiveTab('analytics')}
              >
                <FontAwesomeIcon icon={faChartBar} className={styles['tab-icon']} />
                Analytics
              </div>
            </div>
          </div>
  
          {/* Record Tab */}
          {activeTab === 'record' && (
            <div className={styles['record-form']}>
              <div className={styles['record-tabs']}>
                <div 
                  className={`${styles['record-tab']} ${activeRecordTab === 'expense' ? styles.active : ''}`}
                  onClick={() => setActiveRecordTab('expense')}
                >
                  <FontAwesomeIcon icon={faArrowDown} className={styles['tab-icon']} style={{color: '#f44336'}} />
                  Expense
                </div>
                <div 
                  className={`${styles['record-tab']} ${activeRecordTab === 'earning' ? styles.active : ''}`}
                  onClick={() => setActiveRecordTab('earning')}
                >
                  <FontAwesomeIcon icon={faArrowUp} className={styles['tab-icon']} style={{color: '#4caf50'}} />
                  Earning
                </div>
              </div>
              
              {/* Expense Form */}
              {activeRecordTab === 'expense' && (
                <div className={styles['form-grid']}>
                  {/* Event Selection */}
                  <div className={styles['form-group']}>
                    <label>Event</label>
                    <select
                      className={styles['form-input']}
                      value={selectedEventForRecord?.id || ''}
                      onChange={(e) => {
                        const eventId = e.target.value;
                        const selectedEvent = userEvents.find(event => event.id === eventId);
                        setSelectedEventForRecord(selectedEvent || null);
                      }}
                    >
                      <option value="">Select an event</option>
                      {userEvents.map((event) => (
                        <option key={event.id} value={event.id}>
                          {event.tourName || event.showName || 'Unnamed Event'}
                        </option>
                      ))}
                    </select>   
                    </div>
                  
                  <div className={styles['form-group']}>
                    <label>Amount</label>
                    <div className={styles['form-input-group']}>
                      <span className={styles['currency-symbol']}>₹</span>
                      <input
                        type="number"
                        className={styles['form-input']}
                        value={amount}
                        onChange={(e) => setAmount(e.target.value)}
                        placeholder="Enter amount"
                      />
                    </div>
                  </div>
                  
                  <div className={styles['form-group']}>
                    <label>Date</label>
                    <select
                      className={styles['form-input']}
                      value={selectedDateValue}
                      onChange={handleDateChange}
                      disabled={!selectedEventForRecord}
                    >
                      <option value="">Select date</option>
                      {selectedEventForRecord?.eventDates?.map((date) => {
                        const displayDate = getFormattedDate(date);
                        const venue = getVenueDetails(date);
                        return (
                          <option key={date.id} value={date.date}>
                            {moment(displayDate).format('DD-MM-YYYY')} - {venue.venueName || 'Unknown Venue'} ({venue.formattedAddress || 'No address'})
                            {date.rescheduleDate?.isRescheduled && ' (Rescheduled)'}
                          </option>
                        );
                      })}
                    </select>
                  </div>
  
                  <div className={styles['form-group']}>
                    <label>Category</label>
                    <select
                      className={styles['form-input']}
                      onChange={(e) => setFilterCategory(e.target.value)}
                      defaultValue="Other"
                    >
                      {expenseCategories.filter(cat => cat.id !== 'all').map(category => (
                        <option key={category.id} value={category.id}>
                          {category.name}
                        </option>
                      ))}
                    </select>
                  </div>
  
                  <div className={styles['form-group']}>
                    <label>Note</label>
                    <input
                      type="text"
                      className={styles['form-input']}
                      value={note}
                      onChange={(e) => e.target.value.length <= MAX_NOTE_LENGTH && setNote(e.target.value)}
                      placeholder="Add a note"
                      maxLength={MAX_NOTE_LENGTH}
                    />
                    <span className={styles['char-count']}>{note.length}/{MAX_NOTE_LENGTH}</span>
                  </div>
  
                  <div className={styles['form-group']}>
                    <label className={styles['checkbox-label']}>
                      <input
                        type="checkbox"
                        checked={isSelfExpense}
                        onChange={() => setIsSelfExpense(!isSelfExpense)}
                        className={styles['checkbox-input']}
                      />
                      Mark as self expense
                    </label>
                  </div>
  
                  {!isSelfExpense && (
                    <div className={styles['form-group']}>
                      <label>Split With</label>
                      <div className={styles['users-grid']}>
                        {allUsers.map((user) => (
                          <div key={user.userId} className={styles['user-item']}>
                            <div className={styles['user-select']}>
                              <div className={styles['checkbox-container']}>
                                <input
                                  type="checkbox"
                                  checked={whoOwes.some(u => u.userId === user.userId)}
                                  onChange={() => toggleUserOwes(user)}
                                  className={styles['checkbox-input']}
                                />
                                  
                                <img 
                                  src={user.profilePictureUrl || defaultUserProfilePhoto} 
                                  alt={user.profilePictureUrl ? "Profile" : "Default Profile"}
                                  className={styles['profile-picture']} 
                                />
                                <span className={styles['user-name']}>
                                  {user.fullName}
                                  {user.isCurrentUser && " (You)"}
                                  {user.userType === "Manager" && " (Manager)"}
                                </span>
                              </div>
                              {whoOwes.some(u => u.userId === user.userId) && (
                                <input
                                  type="number"
                                  className={styles['amount-input']}
                                  value={whoOwes.find(u => u.userId === user.userId).amountOwed}
                                  onChange={(e) => updateUserAmountOwed(user.userId, e.target.value)}
                                  placeholder="Amount"
                                />
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
  
                  <button 
                    className={styles['action-button']}
                    onClick={handleSubmit}
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <><FontAwesomeIcon icon={faSpinner} spin /> Processing...</>
                    ) : (
                      <>
                        <FontAwesomeIcon icon={faReceipt} />
                        Submit Expense
                      </>
                    )}
                  </button>
                </div>
              )}
              
              {/* Earning Form */}
              {activeRecordTab === 'earning' && (
                <div className={styles['form-grid']}>
                  {/* Event Selection */}
                  <div className={styles['form-group']}>
                    <label>Event</label>
                    <select
                      className={styles['form-input']}
                      value={selectedEventForRecord?.id || ''}
                      onChange={(e) => {
                        const eventId = e.target.value;
                        const selectedEvent = userEvents.find(event => event.id === eventId);
                        setSelectedEventForRecord(selectedEvent || null);
                      }}
                    >
                      <option value="">Select an event</option>
                      {userEvents.map((event) => (
                        <option key={event.id} value={event.id}>
                          {event.tourName || event.showName || 'Unnamed Event'}
                        </option>
                      ))}
                    </select>
                  </div>
                  
                  {/* User Selection for Earnings */}
                  <div className={styles['form-group']}>
                    <label>Record Earning For</label>
                    <div className={styles['earning-selector']}>
                      <label className={styles['checkbox-label']}>
                        <input
                          type="checkbox"
                          checked={isSelfEarning}
                          onChange={() => {
                            setIsSelfEarning(!isSelfEarning);
                            if (!isSelfEarning) {
                              setEarningForUserId(userData.userId);
                            }
                          }}
                          className={styles['checkbox-input']}
                        />
                        Record my own earning
                      </label>
                    </div>
                    
                    {!isSelfEarning && (
                      <>
                        {hasFinancialPermission ? (
                          <select
                            className={styles['form-input']}
                            value={earningForUserId}
                            onChange={(e) => setEarningForUserId(e.target.value)}
                          >
                            <option value="">Select a user</option>
                            {allUsers.map((user) => (
                              <option key={user.userId} value={user.userId}>
                                {user.fullName}
                                {user.isCurrentUser && " (You)"}
                                {user.userType && ` (${user.userType})`}
                              </option>
                            ))}
                          </select>
                        ) : (
                          <div className={styles['permission-warning']}>
                            <FontAwesomeIcon icon={faLock} className={styles['lock-icon']} />
                            <span>You don't have permission to record earnings for other users</span>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                  
                  <div className={styles['form-group']}>
                    <label>Amount</label>
                    <div className={styles['form-input-group']}>
                      <span className={styles['currency-symbol']}>₹</span>
                      <input
                        type="number"
                        className={styles['form-input']}
                        value={earningAmount}
                        onChange={(e) => setEarningAmount(e.target.value)}
                        placeholder="Enter amount"
                      />
                    </div>
                  </div>
                  
                  <div className={styles['form-group']}>
                    <label>Date</label>
                    <select
                      className={styles['form-input']}
                      value={selectedDateValue}
                      onChange={handleDateChange}
                      disabled={!selectedEventForRecord}
                    >
                      <option value="">Select date</option>
                      {selectedEventForRecord?.eventDates?.map((date) => {
                        const displayDate = getFormattedDate(date);
                        const venue = getVenueDetails(date);
                        return (
                          <option key={date.id} value={date.date}>
                            {moment(displayDate).format('DD-MM-YYYY')} - {venue.venueName || 'Unknown Venue'} ({venue.formattedAddress || 'No address'})
                            {date.rescheduleDate?.isRescheduled && ' (Rescheduled)'}
                          </option>
                        );
                      })}
                    </select>
                  </div>
  
                  <div className={styles['form-group']}>
                    <label>Category</label>
                    <select
                      className={styles['form-input']}
                      value={earningCategory}
                      onChange={(e) => setEarningCategory(e.target.value)}
                    >
                      {earningCategories.map(category => (
                        <option key={category.id} value={category.id}>
                          {category.name}
                        </option>
                      ))}
                    </select>
                  </div>
  
                  <div className={styles['form-group']}>
                    <label>Source</label>
                    <input
                      type="text"
                      className={styles['form-input']}
                      value={earningSource}
                      onChange={(e) => setEarningSource(e.target.value)}
                      placeholder="E.g., Venue name, sponsor, etc."
                    />
                  </div>
  
                  <div className={styles['form-group']}>
                    <label>Note</label>
                    <input
                      type="text"
                      className={styles['form-input']}
                      value={earningNote}
                      onChange={(e) => e.target.value.length <= MAX_NOTE_LENGTH && setEarningNote(e.target.value)}
                      placeholder="Add a note"
                      maxLength={MAX_NOTE_LENGTH}
                    />
                    <span className={styles['char-count']}>{earningNote.length}/{MAX_NOTE_LENGTH}</span>
                  </div>
  
                  <button 
                    className={`${styles['action-button']} ${styles.earning}`}
                    onClick={handleSubmit}
                    disabled={isSubmitting || (!isSelfEarning && !hasFinancialPermission)}
                  >
                    {isSubmitting ? (
                      <><FontAwesomeIcon icon={faSpinner} spin /> Processing...</>
                    ) : (
                      <>
                        <FontAwesomeIcon icon={faDollarSign} />
                        Submit Earning
                      </>
                    )}
                  </button>
                </div>
              )}
            </div>
          )}
  
          {/* History Tab */}
          {activeTab === 'history' && (
            <div className={styles['history-content']}>
              {/* Event selector for history */}
              {!selectedHistoryDate && (
                <div className={styles['event-selector']}>
                  <label>Select Event:</label>
                  <select
                    className={styles['form-input']}
                    value={selectedEventForHistory?.id || ''}
                    onChange={(e) => {
                      const eventId = e.target.value;
                      const selectedEvent = userEvents.find(event => event.id === eventId);
                      setSelectedEventForHistory(selectedEvent || null);
                    }}
                  >
                    <option value="">Select an event</option>
                    {userEvents.map((event) => (
                      <option key={event.id} value={event.id}>
                        {event.tourName || event.showName || 'Unnamed Event'}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              
              {selectedHistoryDate ? (
                <div>
                  <button 
                    className={`${styles['action-button']} ${styles.secondary}`}
                    onClick={() => setSelectedHistoryDate(null)}
                  >
                    ← Back to dates
                  </button>
                  <h3 className={styles['history-date']}>
                    {moment(selectedHistoryDate).format('DD-MM-YYYY')}
                    {selectedEventForHistory && ` - ${selectedEventForHistory.tourName || selectedEventForHistory.showName}`}
                  </h3>
                  
                  {/* History type tabs */}
                  <div className={styles['history-tabs']}>
                    <div 
                      className={`${styles['history-tab']} ${activeHistoryTab === 'all' ? styles.active : ''}`}
                      onClick={() => setActiveHistoryTab('all')}
                    >
                      <FontAwesomeIcon icon={faExchangeAlt} className={styles['tab-icon']} />
                      All
                    </div>
                    <div 
                      className={`${styles['history-tab']} ${activeHistoryTab === 'expenses' ? styles.active : ''}`}
                      onClick={() => setActiveHistoryTab('expenses')}
                    >
                      <FontAwesomeIcon icon={faArrowDown} className={styles['tab-icon']} style={{color: '#f44336'}} />
                      Expenses
                    </div>
                    <div 
                      className={`${styles['history-tab']} ${activeHistoryTab === 'earnings' ? styles.active : ''}`}
                      onClick={() => setActiveHistoryTab('earnings')}
                    >
                      <FontAwesomeIcon icon={faArrowUp} className={styles['tab-icon']} style={{color: '#4caf50'}} />
                      Earnings
                    </div>
                    <div 
                      className={`${styles['history-tab']} ${activeHistoryTab === 'charts' ? styles.active : ''}`}
                      onClick={() => setActiveHistoryTab('charts')}
                    >
                      <FontAwesomeIcon icon={faChartPie} className={styles['tab-icon']} />
                      Charts
                    </div>
                  </div>
                  
                  {/* Filter and search controls */}
                  {activeHistoryTab !== 'charts' && (
                    <div className={styles['filter-controls']}>
                      <div className={styles['search-box']}>
                        <input
                          type="text"
                          placeholder="Search financials..."
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                          className={styles['search-input']}
                        />
                        {searchQuery && (
                          <button 
                            className={styles['clear-search']}
                            onClick={() => setSearchQuery('')}
                          >
                            <FontAwesomeIcon icon={faTimes} />
                          </button>
                        )}
                      </div>
                      
                      <div className={styles['filter-options']}>
                        <div className={styles['category-filter']}>
                          <FontAwesomeIcon icon={faFilter} className={styles['filter-icon']} />
                          <select
                            value={filterCategory}
                            onChange={(e) => setFilterCategory(e.target.value)}
                            className={styles['filter-select']}
                          >
                            {activeHistoryTab === 'expenses' || activeHistoryTab === 'all'
                              ? expenseCategories.map(category => (
                                  <option key={category.id} value={category.id}>
                                    {category.name}
                                  </option>
                                ))
                              : earningCategories.map(category => (
                                  <option key={category.id} value={category.id}>
                                    {category.name}
                                  </option>
                                ))
                            }
                          </select>
                        </div>
                        
                        <label className={styles['user-filter-toggle']}>
                          <input
                            type="checkbox"
                            checked={showOnlyUserFinancials}
                            onChange={() => setShowOnlyUserFinancials(!showOnlyUserFinancials)}
                          />
                          <span>Only my financials</span>
                        </label>
                        
                        <label className={styles['user-filter-toggle']}>
                          <input
                            type="checkbox"
                            checked={showDeletedEntries}
                            onChange={() => setShowDeletedEntries(!showDeletedEntries)}
                          />
                          <span>Show deleted entries</span>
                        </label>
                      </div>
                    </div>
                  )}
                  
                  {/* Charts tab content */}
                  {activeHistoryTab === 'charts' && (
                    <div className={styles['charts-container']}>
                      {(filteredFinancials.expenses.length > 0 || filteredFinancials.earnings.length > 0) ? (
                        <>
                     {/* Summary chart */}
                     <div className={styles['chart-section']}>
                            <h3>Financial Summary</h3>
                            <ResponsiveContainer width="100%" height={300}>
                              <BarChart data={chartData.summary}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis dataKey="name" />
                                <YAxis />
                                <Tooltip formatter={(value) => `₹${value.toFixed(2)}`} />
                                <Legend />
                                <Bar dataKey="value" name="Amount" fill="#8884d8" />
                              </BarChart>
                            </ResponsiveContainer>
                          </div>
                          
                          {/* Expenses by Category chart */}
                          {filteredFinancials.expenses.length > 0 && (
                            <div className={styles['chart-section']}>
                              <h3>Expenses by Category</h3>
                              <div className={styles['chart-row']}>
                                <div className={styles['chart-col']}>
                                  <ResponsiveContainer width="100%" height={300}>
                                    <PieChart>
                                      <Pie
                                        data={chartData.expensesByCategory}
                                        cx="50%"
                                        cy="50%"
                                        labelLine={false}
                                        outerRadius={100}
                                        fill="#8884d8"
                                        dataKey="value"
                                        label={({name, percent}) => `${name}: ${(percent * 100).toFixed(0)}%`}
                                      >
                                        {chartData.expensesByCategory.map((entry, index) => (
                                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                        ))}
                                      </Pie>
                                      <Tooltip formatter={(value) => `₹${value.toFixed(2)}`} />
                                      <Legend />
                                    </PieChart>
                                  </ResponsiveContainer>
                                </div>
                                <div className={styles['chart-col']}>
                                  <ResponsiveContainer width="100%" height={300}>
                                    <BarChart data={chartData.expensesByCategory}>
                                      <CartesianGrid strokeDasharray="3 3" />
                                      <XAxis dataKey="name" />
                                      <YAxis />
                                      <Tooltip formatter={(value) => `₹${value.toFixed(2)}`} />
                                      <Bar dataKey="value" name="Amount" fill="#f44336" />
                                    </BarChart>
                                  </ResponsiveContainer>
                                </div>
                              </div>
                            </div>
                          )}
                          
                          {/* Earnings by Category chart */}
                          {filteredFinancials.earnings.length > 0 && (
                            <div className={styles['chart-section']}>
                              <h3>Earnings by Category</h3>
                              <div className={styles['chart-row']}>
                                <div className={styles['chart-col']}>
                                  <ResponsiveContainer width="100%" height={300}>
                                    <PieChart>
                                      <Pie
                                        data={chartData.earningsByCategory}
                                        cx="50%"
                                        cy="50%"
                                        labelLine={false}
                                        outerRadius={100}
                                        fill="#82ca9d"
                                        dataKey="value"
                                        label={({name, percent}) => `${name}: ${(percent * 100).toFixed(0)}%`}
                                      >
                                        {chartData.earningsByCategory.map((entry, index) => (
                                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                        ))}
                                      </Pie>
                                      <Tooltip formatter={(value) => `₹${value.toFixed(2)}`} />
                                      <Legend />
                                    </PieChart>
                                  </ResponsiveContainer>
                                </div>
                                <div className={styles['chart-col']}>
                                  <ResponsiveContainer width="100%" height={300}>
                                    <BarChart data={chartData.earningsByCategory}>
                                      <CartesianGrid strokeDasharray="3 3" />
                                      <XAxis dataKey="name" />
                                      <YAxis />
                                      <Tooltip formatter={(value) => `₹${value.toFixed(2)}`} />
                                      <Bar dataKey="value" name="Amount" fill="#4caf50" />
                                    </BarChart>
                                  </ResponsiveContainer>
                                </div>
                              </div>
                            </div>
                          )}
                        </>
                      ) : (
                        <div className={styles['empty-state']}>
                          <p>No financial data available for charts</p>
                        </div>
                      )}
                    </div>
                  )}
                  
                  {/* Financial records list */}
                  {activeHistoryTab !== 'charts' && (
                    <div className={styles['expense-list']}>
                      {/* Show expenses if tab is 'all' or 'expenses' */}
                      {(activeHistoryTab === 'all' || activeHistoryTab === 'expenses') && 
                        filteredFinancials.expenses.map((expense, index) => (
                          <div 
                            key={expense.id} 
                            className={`${styles['expense-card']} ${styles.expense} ${expense.isDeleted ? styles['deleted-entry'] : ''}`}
                          >
                            <div className={styles['expense-header']}>
                              <div className={styles['expense-info']}>
                                <div className={styles['expense-amount-row']}>
                                  {/* Expense amount - editable when in edit mode */}
                                  {isEditing === expense.id ? (
                                    <div className={styles['edit-amount-input-group']}>
                                      <span className={styles['currency-symbol']}>{expense.currency}</span>
                                      <input
                                        type="number"
                                        className={styles['edit-amount-input']}
                                        value={editedAmount}
                                        onChange={(e) => setEditedAmount(e.target.value)}
                                        placeholder="Enter amount"
                                      />
                                    </div>
                                  ) : (
                                    <p className={styles['expense-amount']}>
                                      {expense.currency} {expense.amount}
                                      {expense.isDeleted && <span className={styles['deleted-tag']}>(Deleted)</span>}
                                    </p>
                                  )}
                                  
                                  <div className={styles['amount-right-elements']}>
                                    <div className={styles['expense-category']}>
                                      {expense.category || 'Other'}
                                    </div>
                                    
                                    {/* Edit/Delete buttons only shown if user has permission */}
                                    {(expense.paidBy === userData.userId || expense.createdBy === userData.userId) && !expense.isDeleted && (
                                      <div className={styles['action-buttons']}>
                                        {isEditing === expense.id ? (
                                          <>
                                            <button
                                              className={styles['save-button']}
                                              onClick={() => handleEditRecord(expense.id, 'expense')}
                                              disabled={isSubmitting}
                                            >
                                              {isSubmitting ? <FontAwesomeIcon icon={faSpinner} spin /> : <FontAwesomeIcon icon={faCheck} />}
                                            </button>
                                            <button
                                              className={styles['cancel-button']}
                                              onClick={() => {
                                                setIsEditing(null);
                                                setEditedAmount('');
                                                setEditedNote('');
                                              }}
                                            >
                                              <FontAwesomeIcon icon={faTimes} />
                                            </button>
                                          </>
                                        ) : (
                                          <>
                                            <button
                                              className={styles['edit-button']}
                                              onClick={() => handleEditRecord(expense.id, 'expense')}
                                            >
                                              <FontAwesomeIcon icon={faEdit} />
                                            </button>
                                            <button
                                              className={styles['delete-button']}
                                              onClick={() => handleDeleteFinancialRecord(expense.id, 'expense')}
                                            >
                                              <FontAwesomeIcon icon={faTrash} />
                                            </button>
                                          </>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                </div>
                                <p className={styles['paid-by']}>
                                  Paid by: {allUsers.find(u => u.userId === expense.paidBy)?.fullName}
                                  {expense.paidBy === userData.userId && " (You)"}
                                </p>
                                
                                {/* Note - editable when in edit mode */}
                                {isEditing === expense.id ? (
                                  <input
                                    type="text"
                                    className={styles['edit-note-input']}
                                    value={editedNote}
                                    onChange={(e) => setEditedNote(e.target.value)}
                                    placeholder="Edit note"
                                    maxLength={MAX_NOTE_LENGTH}
                                  />
                                ) : (
                                  expense.note && (
                                    <p className={styles['expense-note']}>{expense.note}</p>
                                  )
                                )}
                                
                                {/* Display venue information if available */}
                                {(expense.venueName || expense.venueAddress || expense.venueData?.displayName || expense.venueDetails?.displayName) && (
                                  <p className={styles['venue-info']}>
                                    Venue: {expense.venueName || expense.venueData?.displayName || expense.venueDetails?.displayName || 'Unknown'} 
                                    {(expense.venueAddress || expense.venueData?.formattedAddress || expense.venueDetails?.formattedAddress) && 
                                      ` (${expense.venueAddress || expense.venueData?.formattedAddress || expense.venueDetails?.formattedAddress})`}
                                  </p>
                                )}
                              </div>
                            </div>
                            
                            <div className={styles['expense-details']}>
                              {expense.isSelfExpense ? (
                                <p className={styles['self-expense']}>Self Expense</p>
                              ) : (
                                <div className={styles['split-details']}>
                                  <h4>Split Details:</h4>
                                  {expense.whoOwes.map((ower, idx) => (
                                    <div key={idx} className={styles['split-item']}>
                                      <div className={styles['split-info']}>
                                        <span>
                                          {ower.fullName}
                                          {ower.userId === userData.userId && " (You)"}
                                        </span>
                                        <span>- {expense.currency} {ower.amountOwed}</span>
                                      </div>
                                      {expense.paidBy === userData.userId && 
                                      !ower.isSettled && 
                                      ower.userId !== userData.userId && 
                                      !expense.isDeleted && (
                                        <button 
                                          className={styles['settle-button']}
                                          onClick={() => handleSettle(expense.id, ower.userId)}
                                          disabled={isSettling}
                                        >
                                          {isSettling ? (
                                            <FontAwesomeIcon icon={faSpinner} spin />
                                          ) : (
                                            'Mark as Settled'
                                          )}
                                        </button>
                                      )}
                                      {ower.isSettled && (
                                        <span className={styles['settled-badge']}>
                                          <FontAwesomeIcon icon={faCheck} /> Settled
                                        </span>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                            
                            {/* Toggle for audit history */}
                            {expense.auditTrail && expense.auditTrail.length > 0 && (
                              <div className={styles['audit-toggle']}>
                                <button
                                  className={styles['audit-button']}
                                  onClick={() => setShowAuditHistory({...showAuditHistory, [expense.id]: !showAuditHistory[expense.id]})}
                                >
                                  <FontAwesomeIcon icon={showAuditHistory[expense.id] ? faCaretUp : faCaretDown} />
                                  {showAuditHistory[expense.id] ? 'Hide History' : 'Show History'}
                                </button>
                                
                                {showAuditHistory[expense.id] && renderAuditHistory(expense.id)}
                              </div>
                            )}
                          </div>
                        ))
                      }
                      
                      {/* Show earnings if tab is 'all' or 'earnings' */}
                      {(activeHistoryTab === 'all' || activeHistoryTab === 'earnings') && 
                        filteredFinancials.earnings.map((earning, index) => (
                          <div 
                            key={earning.id} 
                            className={`${styles['expense-card']} ${styles.earning} ${earning.isDeleted ? styles['deleted-entry'] : ''}`}
                          >
                            <div className={styles['expense-header']}>
                              <div className={styles['expense-info']}>
                                <div className={styles['expense-amount-row']}>
                                  {/* Earning amount - editable when in edit mode */}
                                  {isEditing === earning.id ? (
                                    <div className={styles['edit-amount-input-group']}>
                                      <span className={styles['currency-symbol']}>{earning.currency}</span>
                                      <input
                                        type="number"
                                        className={styles['edit-amount-input']}
                                        value={editedAmount}
                                        onChange={(e) => setEditedAmount(e.target.value)}
                                        placeholder="Enter amount"
                                      />
                                    </div>
                                  ) : (
                                    <p className={`${styles['expense-amount']} ${styles.earning}`}>
                                      + {earning.currency} {earning.amount}
                                      {earning.isDeleted && <span className={styles['deleted-tag']}>(Deleted)</span>}
                                    </p>
                                  )}
                                  
                                  <div className={styles['amount-right-elements']}>
                                    <div className={`${styles['expense-category']} ${styles.earning}`}>
                                      {earning.category || 'Other'}
                                    </div>
                                    
                                    {/* Edit/Delete buttons only shown if user has permission */}
                                    {(earning.earnedBy === userData.userId || earning.createdBy === userData.userId || hasFinancialPermission) && !earning.isDeleted && (
                                      <div className={styles['action-buttons']}>
                                        {isEditing === earning.id ? (
                                          <>
                                            <button
                                              className={styles['save-button']}
                                              onClick={() => handleEditRecord(earning.id, 'earning')}
                                              disabled={isSubmitting}
                                            >
                                              {isSubmitting ? <FontAwesomeIcon icon={faSpinner} spin /> : <FontAwesomeIcon icon={faCheck} />}
                                            </button>
                                            <button
                                              className={styles['cancel-button']}
                                              onClick={() => {
                                                setIsEditing(null);
                                                setEditedAmount('');
                                                setEditedNote('');
                                              }}
                                            >
                                              <FontAwesomeIcon icon={faTimes} />
                                            </button>
                                          </>
                                        ) : (
                                          <>
                                            <button
                                              className={styles['edit-button']}
                                              onClick={() => handleEditRecord(earning.id, 'earning')}
                                            >
                                              <FontAwesomeIcon icon={faEdit} />
                                            </button>
                                            <button
                                              className={styles['delete-button']}
                                              onClick={() => handleDeleteFinancialRecord(earning.id, 'earning')}
                                            >
                                              <FontAwesomeIcon icon={faTrash} />
                                            </button>
                                          </>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                </div>
                                <p className={styles['paid-by']}>
                                  Earned by: {allUsers.find(u => u.userId === earning.earnedBy)?.fullName}
                                  {earning.earnedBy === userData.userId && " (You)"}
                                  {!earning.isSelfRecorded && ` (Recorded by ${earning.recordedByName || 'Unknown'})`}
                                </p>
                                
                                {/* Editable note or display source and note */}
                                {isEditing === earning.id ? (
                                  <input
                                    type="text"
                                    className={styles['edit-note-input']}
                                    value={editedNote}
                                    onChange={(e) => setEditedNote(e.target.value)}
                                    placeholder="Edit note"
                                    maxLength={MAX_NOTE_LENGTH}
                                  />
                                ) : (
                                  <>
                                    {earning.source && (
                                      <p className={styles['expense-note']}>Source: {earning.source}</p>
                                    )}
                                    {earning.note && (
                                      <p className={styles['expense-note']}>{earning.note}</p>
                                    )}
                                  </>
                                )}
                                
                                {/* Display venue information if available */}
                                {(earning.venueName || earning.venueAddress || earning.venueData?.displayName || earning.venueDetails?.displayName) && (
                                  <p className={styles['venue-info']}>
                                    Venue: {earning.venueName || earning.venueData?.displayName || earning.venueDetails?.displayName || 'Unknown'} 
                                    {(earning.venueAddress || earning.venueData?.formattedAddress || earning.venueDetails?.formattedAddress) && 
                                      ` (${earning.venueAddress || earning.venueData?.formattedAddress || earning.venueDetails?.formattedAddress})`}
                                  </p>
                                )}
                              </div>
                            </div>
                            
                            {/* Toggle for audit history */}
                            {earning.auditTrail && earning.auditTrail.length > 0 && (
                              <div className={styles['audit-toggle']}>
                                <button
                                  className={styles['audit-button']}
                                  onClick={() => setShowAuditHistory({...showAuditHistory, [earning.id]: !showAuditHistory[earning.id]})}
                                >
                                  <FontAwesomeIcon icon={showAuditHistory[earning.id] ? faCaretUp : faCaretDown} />
                                  {showAuditHistory[earning.id] ? 'Hide History' : 'Show History'}
                                </button>
                                
                                {showAuditHistory[earning.id] && renderAuditHistory(earning.id)}
                              </div>
                            )}
                          </div>
                        ))
                      }
                      
                      {/* Empty state */}
                      {((activeHistoryTab === 'expenses' && filteredFinancials.expenses.length === 0) ||
                        (activeHistoryTab === 'earnings' && filteredFinancials.earnings.length === 0) ||
                        (activeHistoryTab === 'all' && 
                         filteredFinancials.expenses.length === 0 && 
                         filteredFinancials.earnings.length === 0)) && (
                        <div className={styles['empty-state']}>
                          {searchQuery || filterCategory !== 'all' ? (
                            <p>No financial records match your filters</p>
                          ) : (
                            <p>No financial records found for this date</p>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ) : (
                <div className={styles['dates-list']}>
                {datesWithFinancials.length > 0 ? (
                    datesWithFinancials.map((date) => (
                      <div
                        key={date}
                        className={styles['date-card']}
                        onClick={() => setSelectedHistoryDate(date)}
                      >
                        <div className={styles['date-card-content']}>
                          <FontAwesomeIcon icon={faCalendarAlt} className={styles['date-icon']} />
                          <div className={styles['date-info']}>
                            <span className={styles['date']}>
                              {moment(date).format('DD-MM-YYYY')}
                            </span>
                            <span className={styles['expense-count']}>
                              {financialHistory[date]?.expenses?.length || 0} expenses, {financialHistory[date]?.earnings?.length || 0} earnings
                            </span>
                          </div>
                          <FontAwesomeIcon icon={faChevronRight} className={styles['chevron-icon']} />
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className={styles['empty-state']}>
                      <FontAwesomeIcon icon={faReceipt} className={styles['empty-icon']} />
                      <p>
                        {selectedEventForHistory ? (
                          showOnlyUserFinancials 
                            ? `You don't have any financial records for this event yet` 
                            : `No financial records found for this event`
                        ) : (
                          "Please select an event to view financial history"
                        )}
                      </p>
                      {selectedEventForHistory && (
                        <button 
                          className={styles['action-button']}
                          onClick={() => setActiveTab('record')}
                        >
                          <FontAwesomeIcon icon={faPlus} /> Add Your First Record
                        </button>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
          
        {/* Analytics Tab */}
        {activeTab === 'analytics' && (
            <div className={styles['analytics-content']}>
            <div className={styles['analytics-tabs']}>
                <div 
                  className={`${styles['analytics-tab']} ${activeAnalyticsTab === 'summary' ? styles.active : ''}`}
                  onClick={() => setActiveAnalyticsTab('summary')}
                >
                  <FontAwesomeIcon icon={faChartLine} className={styles['tab-icon']} />
                  Summary
                </div>
                <div 
                  className={`${styles['analytics-tab']} ${activeAnalyticsTab === 'byDate' ? styles.active : ''}`}
                  onClick={() => setActiveAnalyticsTab('byDate')}
                >
                  <FontAwesomeIcon icon={faCalendarAlt} className={styles['tab-icon']} />
                  By Date
                </div>
                <div 
                  className={`${styles['analytics-tab']} ${activeAnalyticsTab === 'monthly' ? styles.active : ''}`}
                  onClick={() => setActiveAnalyticsTab('monthly')}
                >
                  <FontAwesomeIcon icon={faCalendarDay} className={styles['tab-icon']} />
                  Monthly
                </div>
                <div 
                  className={`${styles['analytics-tab']} ${activeAnalyticsTab === 'yearly' ? styles.active : ''}`}
                  onClick={() => setActiveAnalyticsTab('yearly')}
                >
                  <FontAwesomeIcon icon={faCalendarWeek} className={styles['tab-icon']} />
                  Yearly
                </div>
                <div 
                  className={`${styles['analytics-tab']} ${activeAnalyticsTab === 'events' ? styles.active : ''}`}
                  onClick={() => setActiveAnalyticsTab('events')}
                >
                  <FontAwesomeIcon icon={faCalendarCheck} className={styles['tab-icon']} />
                  By Event
                </div>
                <div 
                  className={`${styles['analytics-tab']} ${activeAnalyticsTab === 'cities' ? styles.active : ''}`}
                  onClick={() => setActiveAnalyticsTab('cities')}
                >
                  <FontAwesomeIcon icon={faMapMarkerAlt} className={styles['tab-icon']} />
                  By City
                </div>
              </div>
              
              {isLoadingAnalytics ? (
                <div className={styles['loading-analytics']}>
                  <FontAwesomeIcon icon={faSpinner} spin size="2x" />
                  <p>Loading analytics data...</p>
                </div>
              ) : (
                <>
                  {/* Summary View */}
                  {activeAnalyticsTab === 'summary' && (
                    <div className={styles['analytics-summary']}>
                      <div className={styles['summary-metrics']}>
                        <div className={styles['metric-card']}>
                          <h4>Total Earnings</h4>
                          <p className={styles['metric-value']}>{currency} {totalEarnings.toFixed(2)}</p>
                          <p className={styles['metric-subtitle']}>Lifetime earnings</p>
                        </div>
                        <div className={styles['metric-card']}>
  <h4>Total Expenses</h4>
  <p className={styles['metric-value']}>
    {currency} {totalExpense.toFixed(2)}
  </p>
  <div className={styles['amount-breakdown']}>
    <span className={styles['breakdown-item']}>
      Personal: {currency} {(totalExpense - totalRecoverableExpense).toFixed(2)}
    </span>
    <span className={styles['breakdown-item']}>
      Recoverable: {currency} {totalRecoverableExpense.toFixed(2)}
    </span>
    <span className={styles['breakdown-item']}>
      After Settlement: {currency} {(totalExpense - totalRecoverableExpense).toFixed(2)}
    </span>
  </div>
  <p className={styles['metric-subtitle']}>Lifetime expenses</p>
</div>
                        <div className={styles['metric-card']}>
                          <h4>Net Earnings</h4>
                          <p className={styles['metric-value']} style={{color: netEarnings >= 0 ? '#4caf50' : '#f44336'}}>
                            {currency} {netEarnings.toFixed(2)}
                          </p>
                          <p className={styles['metric-subtitle']}>Overall profit/loss</p>
                        </div>
                      </div>
                      
                      {analyticsData.byMonth.length > 0 && (
                        <div className={styles['chart-container']}>
                          <h3>Financial Overview (Last 12 Months)</h3>
                          <ResponsiveContainer width="100%" height={300}>
                            <BarChart
                              data={analyticsData.byMonth.slice(-12)}
                              margin={{
                                top: 20,
                                right: 30,
                                left: 20,
                                bottom: 50,
                              }}
                            >
                              <CartesianGrid strokeDasharray="3 3" />
                              <XAxis dataKey="name" angle={-45} textAnchor="end" height={60} />
                              <YAxis />
                              <Tooltip formatter={(value) => `₹${value.toFixed(2)}`} />
                              <Legend />
                              <Bar dataKey="earnings" name="Earnings" fill="#4caf50" />
                              <Bar dataKey="expenses" name="Expenses" fill="#f44336" />
                              <Bar dataKey="net" name="Net" fill="#2196f3" />
                            </BarChart>
                          </ResponsiveContainer>
                        </div>
                      )}
                      
                      {analyticsData.byCity.length > 0 && (
                        <div className={styles['chart-container']}>
                          <h3>Top Cities by Net Earnings</h3>
                          <ResponsiveContainer width="100%" height={300}>
                            <BarChart
                              data={analyticsData.byCity.sort((a, b) => b.net - a.net).slice(0, 5)}
                              margin={{
                                top: 20,
                                right: 30,
                                left: 20,
                                bottom: 50,
                              }}
                            >
                              <CartesianGrid strokeDasharray="3 3" />
                              <XAxis dataKey="name" />
                              <YAxis />
                              <Tooltip formatter={(value) => `₹${value.toFixed(2)}`} />
                              <Legend />
                              <Bar dataKey="net" name="Net Earnings" fill="#2196f3" />
                            </BarChart>
                          </ResponsiveContainer>
                        </div>
                      )}
                      
                      {analyticsData.byEvent.length > 0 && (
                        <div className={styles['chart-container']}>
                          <h3>Top Events by Net Earnings</h3>
                          <ResponsiveContainer width="100%" height={300}>
                            <BarChart
                              data={analyticsData.byEvent.sort((a, b) => b.net - a.net).slice(0, 5)}
                              margin={{
                                top: 20,
                                right: 30,
                                left: 20,
                                bottom: 50,
                              }}
                            >
                              <CartesianGrid strokeDasharray="3 3" />
                              <XAxis dataKey="name" />
                              <YAxis />
                              <Tooltip formatter={(value) => `₹${value.toFixed(2)}`} />
                              <Legend />
                              <Bar dataKey="net" name="Net Earnings" fill="#2196f3" />
                            </BarChart>
                          </ResponsiveContainer>
                        </div>
                      )}
                    </div>
                  )}
                  
                  {/* By Date View */}
                  {activeAnalyticsTab === 'byDate' && (
                    <div className={styles['analytics-bydate']}>
                      <h3>Financial Breakdown by Date</h3>
                      
                      {analyticsData.byDate.length > 0 ? (
                        <>
                          <div className={styles['chart-container']}>
                            <ResponsiveContainer width="100%" height={300}>
                              <BarChart
                                data={analyticsData.byDate.slice(0, 30)} // Show last 30 days
                                margin={{
                                  top: 20,
                                  right: 30,
                                  left: 20,
                                  bottom: 50,
                                }}
                              >
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis 
                                  dataKey="date" 
                                  tickFormatter={(date) => moment(date).format('DD-MM')}
                                  angle={-45} 
                                  textAnchor="end" 
                                  height={60} 
                                />
                                <YAxis />
                                <Tooltip 
                                  formatter={(value) => `₹${value.toFixed(2)}`}
                                  labelFormatter={(date) => moment(date).format('DD-MM-YYYY')}
                                />
                                <Legend />
                                <Bar dataKey="earnings" name="Earnings" fill="#4caf50" />
                                <Bar dataKey="expenses" name="Expenses" fill="#f44336" />
                                <Bar dataKey="net" name="Net" fill="#2196f3" />
                              </BarChart>
                            </ResponsiveContainer>
                          </div>
                          
                          <div className={styles['date-table']}>
                            <table>
                              <thead>
                                <tr>
                                  <th>Date</th>
                                  <th>Event</th>
                                  <th>Earnings</th>
                                  <th>Expenses</th>
                                  <th>Net</th>
                                </tr>
                              </thead>
                              <tbody>
                                {analyticsData.byDate.map((date) => (
                                  <tr key={date.date}>
                                    <td>{moment(date.date).format('DD-MM-YYYY')}</td>
                                    <td>{date.eventName}</td>
                                    <td className={styles['earnings-cell']}>{currency} {date.earnings.toFixed(2)}</td>
                                    <td className={styles['expenses-cell']}>{currency} {date.expenses.toFixed(2)}</td>
                                    <td className={date.net >= 0 ? styles['positive-cell'] : styles['negative-cell']}>
                                      {currency} {date.net.toFixed(2)}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </>
                      ) : (
                        <div className={styles['empty-state']}>
                          <p>No date data available yet</p>
                        </div>
                      )}
                    </div>
                  )}
                  
                  {/* Monthly View */}
                  {activeAnalyticsTab === 'monthly' && (
                    <div className={styles['analytics-monthly']}>
                      <h3>Monthly Financial Breakdown</h3>
                      
                      {analyticsData.byMonth.length > 0 ? (
                        <>
                          <div className={styles['chart-container']}>
                            <ResponsiveContainer width="100%" height={300}>
                              <LineChart
                                data={analyticsData.byMonth}
                                margin={{
                                  top: 20,
                                  right: 30,
                                  left: 20,
                                  bottom: 50,
                                }}
                              >
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis dataKey="name" angle={-45} textAnchor="end" height={60} />
                                <YAxis />
                                <Tooltip formatter={(value) => `₹${value.toFixed(2)}`} />
                                <Legend />
                                <Line type="monotone" dataKey="earnings" name="Earnings" stroke="#4caf50" activeDot={{ r: 8 }} />
                                <Line type="monotone" dataKey="expenses" name="Expenses" stroke="#f44336" activeDot={{ r: 8 }} />
                                <Line type="monotone" dataKey="net" name="Net" stroke="#2196f3" activeDot={{ r: 8 }} />
                              </LineChart>
                            </ResponsiveContainer>
                          </div>
                          
                          <div className={styles['chart-container']}>
                            <ResponsiveContainer width="100%" height={300}>
                              <AreaChart
                                data={analyticsData.byMonth}
                                margin={{
                                  top: 20,
                                  right: 30,
                                  left: 20,
                                  bottom: 50,
                                }}
                              >
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis dataKey="name" angle={-45} textAnchor="end" height={60} />
                                <YAxis />
                                <Tooltip formatter={(value) => `₹${value.toFixed(2)}`} />
                                <Legend />
                                <Area type="monotone" dataKey="earnings" name="Earnings" stroke="#4caf50" fill="#4caf50" fillOpacity={0.3} />
                                <Area type="monotone" dataKey="expenses" name="Expenses" stroke="#f44336" fill="#f44336" fillOpacity={0.3} />
                              </AreaChart>
                            </ResponsiveContainer>
                          </div>
                          
                          <div className={styles['monthly-table']}>
                            <table>
                              <thead>
                                <tr>
                                  <th>Month</th>
                                  <th>Earnings</th>
                                  <th>Expenses</th>
                                  <th>Net</th>
                                </tr>
                              </thead>
                              <tbody>
                                {analyticsData.byMonth.slice().reverse().map((month) => (
                                  <tr key={month.id}>
                                    <td>{month.name}</td>
                                    <td className={styles['earnings-cell']}>{currency} {month.earnings.toFixed(2)}</td>
                                    <td className={styles['expenses-cell']}>{currency} {month.expenses.toFixed(2)}</td>
                                    <td className={month.net >= 0 ? styles['positive-cell'] : styles['negative-cell']}>
                                      {currency} {month.net.toFixed(2)}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </>
                      ) : (
                        <div className={styles['empty-state']}>
                          <p>No monthly data available yet</p>
                        </div>
                      )}
                    </div>
                  )}
                  
                  {/* Yearly View */}
                  {activeAnalyticsTab === 'yearly' && (
                    <div className={styles['analytics-yearly']}>
                      <h3>Yearly Financial Overview</h3>
                      
                      {analyticsData.byYear.length > 0 ? (
                        <>
                          <div className={styles['chart-container']}>
                            <ResponsiveContainer width="100%" height={300}>
                              <BarChart
                                data={analyticsData.byYear}
                                margin={{
                                  top: 20,
                                  right: 30,
                                  left: 20,
                                  bottom: 30,
                                }}
                              >
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis dataKey="name" />
                                <YAxis />
                                <Tooltip formatter={(value) => `₹${value.toFixed(2)}`} />
                                <Legend />
                                <Bar dataKey="earnings" name="Earnings" fill="#4caf50" />
                                <Bar dataKey="expenses" name="Expenses" fill="#f44336" />
                                <Bar dataKey="net" name="Net" fill="#2196f3" />
                              </BarChart>
                            </ResponsiveContainer>
                          </div>
                          
                          <div className={styles['yearly-table']}>
                            <table>
                              <thead>
                                <tr>
                                  <th>Year</th>
                                  <th>Earnings</th>
                                  <th>Expenses</th>
                                  <th>Net</th>
                                </tr>
                              </thead>
                              <tbody>
                                {analyticsData.byYear.slice().reverse().map((year) => (
                                  <tr key={year.year}>
                                    <td>{year.name}</td>
                                    <td className={styles['earnings-cell']}>{currency} {year.earnings.toFixed(2)}</td>
                                    <td className={styles['expenses-cell']}>{currency} {year.expenses.toFixed(2)}</td>
                                    <td className={year.net >= 0 ? styles['positive-cell'] : styles['negative-cell']}>
                                      {currency} {year.net.toFixed(2)}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </>
                      ) : (
                        <div className={styles['empty-state']}>
                          <p>No yearly data available yet</p>
                        </div>
                      )}
                    </div>
                  )}
                  
                  {/* Events View */}
                  {activeAnalyticsTab === 'events' && (
                    <div className={styles['analytics-events']}>
                      <h3>Financial Breakdown by Event</h3>
                      
                      {analyticsData.byEvent.length > 0 ? (
                        <>
                          <div className={styles['chart-container']}>
                            <h4>Top Events by Net Earnings</h4>
                            <ResponsiveContainer width="100%" height={300}>
                              <BarChart
                                data={analyticsData.byEvent.sort((a, b) => b.net - a.net).slice(0, 10)}
                                margin={{
                                  top: 20,
                                  right: 30,
                                  left: 20,
                                  bottom: 50,
                                }}
                                layout="vertical"
                              >
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis type="number" />
                                <YAxis type="category" dataKey="name" width={150} />
                                <Tooltip formatter={(value) => `₹${value.toFixed(2)}`} />
                                <Legend />
                                <Bar dataKey="net" name="Net Earnings" fill="#2196f3" />
                              </BarChart>
                            </ResponsiveContainer>
                            
                            {analyticsData.byEvent.length > 10 && !moreEventsVisible && (
                              <div className={styles['more-button-container']}>
                                <button 
                                  className={styles['more-button']}
                                  onClick={() => setMoreEventsVisible(true)}
                                >
                                  Show More Events
                                </button>
                              </div>
                            )}
                            
                            {moreEventsVisible && (
                              <div className={styles['chart-container']}>
                                <h4>Additional Events</h4>
                                <ResponsiveContainer width="100%" height={300}>
                                  <BarChart
                                    data={analyticsData.byEvent.sort((a, b) => b.net - a.net).slice(10, 20)}
                                    margin={{
                                      top: 20,
                                      right: 30,
                                      left: 20,
                                      bottom: 50,
                                    }}
                                    layout="vertical"
                                  >
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis type="number" />
                                    <YAxis type="category" dataKey="name" width={150} />
                                    <Tooltip formatter={(value) => `₹${value.toFixed(2)}`} />
                                    <Legend />
                                    <Bar dataKey="net" name="Net Earnings" fill="#2196f3" />
                                  </BarChart>
                                </ResponsiveContainer>
                              </div>
                            )}
                          </div>
                          
                          {/* Event details for selected event */}
                          {selectedEventForEventAnalytics && (
                            <div className={styles['event-detail-section']}>
                              <h4>Date Breakdown for {selectedEventForEventAnalytics.name}</h4>
                              <div className={styles['chart-container']}>
                                <ResponsiveContainer width="100%" height={300}>
                                  <BarChart
                                    data={getEventDateDetails(selectedEventForEventAnalytics.id)}
                                    margin={{
                                      top: 20,
                                      right: 30,
                                      left: 20,
                                      bottom: 50,
                                    }}
                                  >
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis 
                                      dataKey="date" 
                                      tickFormatter={(date) => moment(date).format('DD-MM')}
                                      angle={-45} 
                                      textAnchor="end" 
                                      height={60} 
                                    />
                                    <YAxis />
                                    <Tooltip 
                                      formatter={(value) => `₹${value.toFixed(2)}`}
                                      labelFormatter={(date) => moment(date).format('DD-MM-YYYY')}
                                    />
                                    <Legend />
                                    <Bar dataKey="earnings" name="Earnings" fill="#4caf50" />
                                    <Bar dataKey="expenses" name="Expenses" fill="#f44336" />
                                    <Bar dataKey="net" name="Net" fill="#2196f3" />
                                  </BarChart>
                                </ResponsiveContainer>
                              </div>
                              
                              <button 
                                className={`${styles['action-button']} ${styles.secondary}`}
                                onClick={() => setSelectedEventForEventAnalytics(null)}
                              >
                                Back to Events List
                              </button>
                            </div>
                          )}
                          
                          {!selectedEventForEventAnalytics && (
                            <div className={styles['events-table']}>
                              <table>
                                <thead>
                                  <tr>
                                    <th>Event</th>
                                    <th>Earnings</th>
                                    <th>Expenses</th>
                                    <th>Net</th>
                                    <th>Actions</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {analyticsData.byEvent.map((event) => (
                                    <tr key={event.id}>
                                      <td>{event.name}</td>
                                      <td className={styles['earnings-cell']}>{currency} {event.earnings.toFixed(2)}</td>
                                      <td className={styles['expenses-cell']}>{currency} {event.expenses.toFixed(2)}</td>
                                      <td className={event.net >= 0 ? styles['positive-cell'] : styles['negative-cell']}>
                                        {currency} {event.net.toFixed(2)}
                                      </td>
                                      <td>
                                        <button 
                                          className={styles['detail-button']}
                                          onClick={() => setSelectedEventForEventAnalytics(event)}
                                        >
                                          View Details
                                        </button>
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          )}
                        </>
                      ) : (
                        <div className={styles['empty-state']}>
                          <p>No event data available yet</p>
                        </div>
                      )}
                    </div>
                  )}
                  
                  {/* Cities View */}
                  {activeAnalyticsTab === 'cities' && (
                    <div className={styles['analytics-cities']}>
                      <h3>Financial Breakdown by City</h3>
                      
                      {analyticsData.byCity.length > 0 ? (
                        <>
                          <div className={styles['chart-container']}>
                            <h4>Top Cities by Net Earnings</h4>
                            <ResponsiveContainer width="100%" height={300}>
                              <BarChart
                                data={analyticsData.byCity.sort((a, b) => b.net - a.net).slice(0, 5)}
                                margin={{
                                  top: 20,
                                  right: 30,
                                  left: 20,
                                  bottom: 30,
                                }}
                                layout="vertical"
                              >
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis type="number" />
                                <YAxis type="category" dataKey="name" width={150} />
                                <Tooltip formatter={(value) => `₹${value.toFixed(2)}`} />
                                <Legend />
                                <Bar dataKey="net" name="Net Earnings" fill="#2196f3" />
                              </BarChart>
                            </ResponsiveContainer>
                          </div>
                          
                          <div className={styles['chart-container']}>
                            <h4>Cities Earnings vs Expenses</h4>
                            <ResponsiveContainer width="100%" height={300}>
                              <BarChart
                                data={analyticsData.byCity.sort((a, b) => b.net - a.net).slice(0, 10)}
                                margin={{
                                  top: 20,
                                  right: 30,
                                  left: 20,
                                  bottom: 60,
                                }}
                              >
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis dataKey="name" angle={-45} textAnchor="end" height={60} />
                                <YAxis />
                                <Tooltip formatter={(value) => `₹${value.toFixed(2)}`} />
                                <Legend />
                                <Bar dataKey="earnings" name="Earnings" fill="#4caf50" />
                                <Bar dataKey="expenses" name="Expenses" fill="#f44336" />
                              </BarChart>
                            </ResponsiveContainer>
                          </div>
                          
                          <div className={styles['cities-table']}>
                            <table>
                              <thead>
                                <tr>
                                  <th>City</th>
                                  <th>Venue</th>
                                  <th>Earnings</th>
                                  <th>Expenses</th>
                                  <th>Net</th>
                                </tr>
                              </thead>
                              <tbody>
                                {analyticsData.byCity.map((city) => (
                                  <tr key={city.id}>
                                    <td>{city.name}</td>
                                    <td>{city.venueName || 'Various Venues'}</td>
                                    <td className={styles['earnings-cell']}>{currency} {city.earnings.toFixed(2)}</td>
                                    <td className={styles['expenses-cell']}>{currency} {city.expenses.toFixed(2)}</td>
                                    <td className={city.net >= 0 ? styles['positive-cell'] : styles['negative-cell']}>
                                      {currency} {city.net.toFixed(2)}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </>
                      ) : (
                        <div className={styles['empty-state']}>
                          <p>No city data available yet</p>
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>
        {!isMobile && <RightMenu />}
      </div>
      {isMobile && <BottomNav />}


      {/* Date Selection Modal for Report Generation */}
      {showDateSelection && (
        <div className={styles['modal-overlay']}>
          <div className={styles['modal-content']}>
            <div className={styles['modal-header']}>
              <h3>Generate Financial Report</h3>
              <button 
                className={styles['close-button']}
                onClick={() => {
                  setShowDateSelection(false);
                  setSelectedDatesForReport([]);
                  setSelectedEventsForReport([]);
                }}
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
            <div className={styles['modal-body']}>
              <div className={styles['report-type-selector']}>
                <div className={styles['report-types']}>
                  <div 
                    className={`${styles['report-type']} ${reportType === 'dates' ? styles.active : ''}`}
                    onClick={() => setReportType('dates')}
                  >
                    <FontAwesomeIcon icon={faCalendarDay} className={styles['type-icon']} />
                    <span>By Date</span>
                  </div>
                  <div 
                    className={`${styles['report-type']} ${reportType === 'events' ? styles.active : ''}`}
                    onClick={() => setReportType('events')}
                  >
                    <FontAwesomeIcon icon={faCalendarCheck} className={styles['type-icon']} />
                    <span>By Event</span>
                  </div>
                  <div 
                    className={`${styles['report-type']} ${reportType === 'year' ? styles.active : ''}`}
                    onClick={() => setReportType('year')}
                  >
                    <FontAwesomeIcon icon={faCalendarWeek} className={styles['type-icon']} />
                    <span>All Years</span>
                  </div>
                </div>
              </div>
              
              {/* Date selection list */}
              {reportType === 'dates' && (
                <div className={styles['dates-list']}>
                  <h4>Select Dates for Report</h4>
                  {datesWithFinancials.map(date => (
                    <div
                      key={date}
                      className={`${styles['date-selection-item']} ${
                        selectedDatesForReport.includes(date) ? styles.selected : ''
                      }`}
                      onClick={() => {
                        setSelectedDatesForReport(prev =>
                          prev.includes(date) 
                            ? prev.filter(d => d !== date)
                            : [...prev, date]
                        );
                      }}
                    >
                      <span>{moment(date).format('DD-MM-YYYY')}</span>
                      {selectedDatesForReport.includes(date) && (
                        <FontAwesomeIcon icon={faCheck} className={styles['check-icon']} />
                      )}
                    </div>
                  ))}
                </div>
              )}
              
              {/* Event selection list */}
              {reportType === 'events' && (
                <div className={styles['events-list']}>
                  <h4>Select Events for Report</h4>
                  {userEvents.map(event => (
                    <div
                      key={event.id}
                      className={`${styles['event-selection-item']} ${
                        selectedEventsForReport.includes(event.id) ? styles.selected : ''
                      }`}
                      onClick={() => {
                        setSelectedEventsForReport(prev =>
                          prev.includes(event.id) 
                            ? prev.filter(e => e !== event.id)
                            : [...prev, event.id]
                        );
                      }}
                    >
                      <span>{event.tourName || event.showName || 'Unnamed Event'}</span>
                      {selectedEventsForReport.includes(event.id) && (
                        <FontAwesomeIcon icon={faCheck} className={styles['check-icon']} />
                      )}
                    </div>
                  ))}
                </div>
              )}
              
              {/* Year report info */}
              {reportType === 'year' && (
                <div className={styles['year-report-info']}>
                  <h4>Annual Financial Report</h4>
                  <p>This report will include financial data for all years where you have recorded expenses or earnings.</p>
                  <p>Each year will have its own worksheet showing monthly breakdowns and charts.</p>
                  <p>The report will include:</p>
                  <ul>
                    <li>Summary of totals across all years</li>
                    <li>Monthly breakdown for each year</li>
                    <li>Charts for visual analysis</li>
                    <li>Comparison between years</li>
                  </ul>
                </div>
              )}
            </div>
            <div className={styles['modal-footer']}>
              {reportType === 'dates' && (
                <button
                  className={`${styles['action-button']} ${styles.secondary}`}
                  onClick={() => setSelectedDatesForReport(datesWithFinancials)}
                >
                  Select All Dates
                </button>
              )}
              {reportType === 'events' && (
                <button
                  className={`${styles['action-button']} ${styles.secondary}`}
                  onClick={() => setSelectedEventsForReport(userEvents.map(event => event.id))}
                >
                  Select All Events
                </button>
              )}
              <button
                className={styles['action-button']}
                onClick={() => {
                  if (reportType === 'dates' && selectedDatesForReport.length === 0) {
                    alert('Please select at least one date');
                    return;
                  }
                  if (reportType === 'events' && selectedEventsForReport.length === 0) {
                    alert('Please select at least one event');
                    return;
                  }
                  generateExcelReport();
                  setShowDateSelection(false);
                }}
              >
                Generate Report
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Loading Overlay */}
      {(isSubmitting || isSettling || isLoadingFinancials) && (
        <div className={styles['loading-overlay']}>
          <div className={styles['loading-spinner']} />
        </div>
      )}
    </div>
  );
};

export default Financial;